// product-grid-utils.js

window.utils = {
  scrollHandler: null,
  filterParams: "",
  sortParam: "",
  globalUrl: (baseUrl, page, productsPerPage) => {
    return `${baseUrl}&page=${page}&resultsPerPage=${productsPerPage}`;
  },
  paginationData: {},
  currentWrapperData: {},
  parsedMarketingBlocks: {},

  // Function to generate marketing block HTML based on type
  generateMarketingBlock: (block) => {
    const { type, settings } = block;
    const {
      background_color = "transparent",
      text_color = "#ffffff",
      image,
      video,
      overlay_color = "rgba(0,0,0,0.3)",
      overlay_opacity = 30,
      overline,
      heading,
      heading_tag = "h3",
      text,
      link,
      link_2,
      link_text = "Shop Now",
      // Lifestyle block specific settings
      featured_product,
      row_start_desktop = 3,
      column_start_desktop = 1,
      tooltip_placement = "bottom",
      tooltip_offset = 16,
      focus_point_x = 0,
      focus_point_y = 0,
    } = settings;
    // Generate media content
    let mediaContent;
    if (video.url) {
      mediaContent = `
      <div class="video-container">
        <video class="marketing-video" autoplay loop muted playsinline webkit-playsinline>
          <source src="${video.url}" type="video/mp4">
        </video>
      </div>
    `;
    } else {
      mediaContent = `<img src="${
        image || "//via.placeholder.com/800x800"
      }" loading="lazy" alt="${heading || ""}">`;
    }

    // Base classes and attributes
    let blockClasses = [
      "marketing-block",
      `marketing-block--${type.replace("marketing-block--", "")}`,
    ];
    let blockAttributes = "";
    let blockStyles = `background-color: ${background_color}; color: ${text_color};`;

    // Type-specific modifications
    if (type === "marketing-block--wide") {
      blockClasses.push("marketing-block--span-2");
    } else if (type === "marketing-block--life") {
      blockAttributes = `
      data-tooltip-placement="${tooltip_placement}"
      data-tooltip-offset="${tooltip_offset}"
      data-focus-point-x="${focus_point_x}"
      data-focus-point-y="${focus_point_y}"
      data-row-start-desktop="${row_start_desktop}"
      data-column-start-desktop="${column_start_desktop}"
    `;
    }

    // Content with conditional rendering
    const contentHtml = `
    ${
      overline ? `<div class="marketing-block__overline">${overline}</div>` : ""
    }
    ${
      heading
        ? `<${heading_tag} class="marketing-block__heading">${heading}</${heading_tag}>`
        : ""
    }
    ${text ? `<div class="marketing-block__text">${text}</div>` : ""}
    ${link_text ? `<span class="marketing-block__cta">${link_text}</span>` : ""}
    ${
      featured_product
        ? `<div class="marketing-block__product" data-product-id="${featured_product}"></div>`
        : ""
    }
  `;

    return `
    <div class="${blockClasses.join(
      " "
    )}" style="${blockStyles}" ${blockAttributes}>
      ${
        link
          ? `<a href="${link}" class="marketing-block__link">`
          : '<div class="marketing-block__link">'
      }
        ${
          link_2
            ? `<a href="${link_2}" class="marketing-block__media">`
            : '<div class="marketing-block__media">'
        }
          ${mediaContent}
          ${
            overlay_color
              ? `<div class="marketing-block__overlay" style="background-color: ${overlay_color}; opacity: ${overlay_opacity}%"></div>`
              : ""
          }
        ${link_2 ? "</a>" : "</div>"}
        <div class="marketing-block__content">
          ${contentHtml}
        </div>
      ${link ? "</a>" : "</div>"}
    </div>
  `;
  },

  preventArrowScroll: (e) => {
    const keys = [32, 33, 34, 35, 36, 37, 38, 39, 40]; // space, page up/down, arrows
    if (keys.includes(e.keyCode)) {
      e.preventDefault();
    }
  },

  getSwatchStatus: (() => {
    let lastDataset = null;
    let lastResult = null;

    return function (dataset) {
      // Check if we've already processed this exact dataset object
      if (dataset === lastDataset) {
        return lastResult;
      }

      // Store the new dataset for future checks
      lastDataset = dataset;

      // Find the first truthy showSwatches value in the dataset
      for (const val of Object.values(dataset)) {
        const isShowSwatch = val?.showSwatches?.toLowerCase?.();
        if (isShowSwatch === "true" || isShowSwatch === "false") {
          lastResult = isShowSwatch === "true";
          return lastResult;
        }
      }

      // Default return if no valid value found
      lastResult = null;
      return lastResult;
    };
  })(),

  getAspectRatioFromAnyDataset: (() => {
    let lastValue = null;
    let lastRatio = null;

    const ratios = {
      natural: 1,
      square: 1,
      landscape: 16 / 9,
      portrait: 9 / 16,
      widescreen: 16 / 9,
      ultrawide: 21 / 9,
      standard: 4 / 3,
      cinematic: 2.39 / 1,
      instagram: 4 / 5,
      reels: 9 / 16,
      stories: 9 / 16,
      facebook: 1.91 / 1,
      linkedin: 1.91 / 1,
      youtube: 16 / 9,
      tiktok: 9 / 16,
      pinterest: 2 / 3,
      banner: 3 / 1,
      cover: 2.63 / 1,
    };

    return function (dataset) {
      for (const val of Object.values(dataset)) {
        const aspect = val?.aspectRatio?.toLowerCase?.();
        if (ratios[aspect]) {
          if (aspect === lastValue) return lastRatio;
          lastValue = aspect;
          lastRatio = ratios[aspect];
          return lastRatio;
        }
      }
      return null;
    };
  })(),

  toggleScroll: (freeze = true) => {
    if (freeze) {
      window.utils.scrollHandler = function (e) {
        e.preventDefault();
      };

      window.addEventListener("scroll", window.utils.scrollHandler, {
        passive: false,
      });
      window.addEventListener("wheel", window.utils.scrollHandler, {
        passive: false,
      });
      window.addEventListener("touchmove", window.utils.scrollHandler, {
        passive: false,
      });
      window.addEventListener(
        "keydown",
        window.utils.preventArrowScroll,
        false
      );
    } else {
      window.removeEventListener("scroll", window.utils.scrollHandler);
      window.removeEventListener("wheel", window.utils.scrollHandler);
      window.removeEventListener("touchmove", window.utils.scrollHandler);
      window.removeEventListener("keydown", window.utils.preventArrowScroll);
      window.utils.scrollHandler = null;
    }
  },

  formatPrice: (price) => {
    if (!price) return "";

    const numericPrice = typeof price === "string" ? parseFloat(price) : price;

    // Check if price is a whole number
    if (Number.isInteger(numericPrice)) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numericPrice);
    }

    // For prices with cents
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericPrice);
  },

  // Determine if a product is a rug
  isRugProduct: function (product) {
    return (
      product?.product_type_unigram?.toLowerCase() === "rug" ||
      product?.name?.toLowerCase().includes("rug")
    );
  },

  // Extract size from product title
  extractSizeFromTitle: function (title) {
    const sizeMatch = title.match(
      /(\d{1,2}(?:'|\u0026quot;)\d{0,2}(?:\u0026quot;)?\s*x\s*\d{1,2}(?:'|\u0026quot;)\d{0,2}(?:\u0026quot;)?)/i
    );
    if (sizeMatch) {
      return sizeMatch[0].replace(/\\u0026quot;/g, '"').replace(/"/g, '"');
    }
    return null;
  },

  // Generate price HTML
  generatePriceHTML: function (product) {
    const variants = this.parseHtmlEncodedJsonString(product?.variants?.[0]);

    // Helper function to safely parse and clean prices
    const parsePrice = (priceStr) => {
      if (!priceStr || priceStr.trim() === "") return null;
      const cleaned = priceStr.trim().replace(/\.00$/, "");
      const parsed = Number(cleaned.replace(/[^0-9.]/g, ""));
      return isNaN(parsed) ? null : parsed;
    };

    // Get all variant prices if they exist
    const variantsPrice = product?.variant_price
      ?.split("|")
      .map((item) => parsePrice(item))
      .filter((price) => price !== null && price > 0);

    // Get standard prices
    const originalPrice = parsePrice(product.msrp);
    const currentPrice = parsePrice(product.price);
    // const isOnSale = originalPrice && currentPrice && currentPrice < originalPrice;
    const toNumber = (value) => {
      if (!value) return 0;
      const num = parseFloat(value);
      return num > 1000 ? num / 100 : num;
    };

    const isOnSale =
      variants.length > 1
        ? variants.some(
            (v) =>
              v.inventory_quantity > 0 &&
              toNumber(v.compare_at_price) > toNumber(v.price)
          ) || currentPrice < originalPrice
        : currentPrice < originalPrice;

    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;

    // Case 1: No valid prices available
    if ((!variantsPrice || variantsPrice.length === 0) && !originalPrice) {
      return "";
    }

    // Case 2: No variant prices - show original price
    if (!variantsPrice || variantsPrice.length === 0) {
      return `<span class="${isOnSale ? "sale-price" : ""}">${utils.formatPrice(
        originalPrice
      )}</span>`;
    }

    // Calculate min/max prices from variants
    const minPrice = Math.min(...variantsPrice);
    const hasMultipleVariants = variantsPrice.length > 1;
    const hasMultipleVariantIds = product.variant_id.split("|").length > 1;
    const fromText = hasMultipleVariants || hasMultipleVariants ? "from " : "";

    // Case 3: Custom rug flow - special pricing calculation
    if (isCustomRugFlow) {
      const rugLength = 2;
      const rugWidth = 5;
      const customRugPrice = minPrice * rugLength * rugWidth;
      return `<span class="${isOnSale ? "sale-price" : ""}">${
        hasMultipleVariantIds ? "from " : ""
      }${utils.formatPrice(customRugPrice)}</span>`;
    }

    // Case 4: Standard price display
    // const isRug = this.isRugProduct(product);
    const isRug = product.product_type_unigram === "rug";
    const formattedPrice = utils.formatPrice(isRug ? minPrice : product.price);
    const safePrice =
      formattedPrice === "$0" ? `$${variantsPrice[0]}` : formattedPrice;

    let priceHTML = `<span class="${
      isOnSale ? "sale-price" : ""
    }">${fromText}${safePrice}</span>`;

    // Add original price if on sale
    if (
      isOnSale &&
      originalPrice &&
      parseInt(minPrice) < parseInt(originalPrice)
    ) {
      priceHTML += `<span class="original-price" style="text-decoration: line-through; margin-left: 5px;">${utils.formatPrice(
        originalPrice
      )}</span>`;
    }

    return priceHTML;
  },

  validateSwatchImages: async function () {
    // Helper function to get dominant color from image
    const getHexColorFromImage = (imageUrl) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = imageUrl;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const targetSize = 50; // Resize image for performance
          canvas.width = targetSize;
          canvas.height = targetSize;
          ctx.drawImage(img, 0, 0, targetSize, targetSize);

          const imageData = ctx.getImageData(0, 0, targetSize, targetSize);
          const data = imageData.data;

          let rTotal = 0,
            gTotal = 0,
            bTotal = 0,
            count = 0;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const alpha = data[i + 3];

            // Ignore transparent and near-white pixels (likely background)
            if (alpha < 128 || (r > 240 && g > 240 && b > 240)) continue;

            rTotal += r;
            gTotal += g;
            bTotal += b;
            count++;
          }

          if (count === 0) {
            reject("No valid pixels found");
            return;
          }

          const avgR = Math.round(rTotal / count);
          const avgG = Math.round(gTotal / count);
          const avgB = Math.round(bTotal / count);

          const rgbToHex = (r, g, b) =>
            "#" +
            [r, g, b].map((val) => val.toString(16).padStart(2, "0")).join("");

          resolve(rgbToHex(avgR, avgG, avgB));
        };

        img.onerror = () => reject("Image load error");
      });
    };

    // Helper function to validate CSS color strings
    function isColorValid(color) {
      if (!color || typeof color !== "string") return false;

      const tempElement = document.createElement("div");
      tempElement.style.color = "transparent";
      tempElement.style.color = color;

      return tempElement.style.color !== "transparent";
    }

    // Helper function to check if image loads successfully
    async function isImageValid(imgUrl) {
      return new Promise((resolve) => {
        const testImg = new Image();
        testImg.crossOrigin = "Anonymous";
        testImg.src = imgUrl;

        testImg.onload = () => resolve(true);
        testImg.onerror = () => resolve(false);
      });
    }

    const swatches = document.querySelectorAll(".new-swatch");

    // Process swatches in parallel
    await Promise.all(
      Array.from(swatches).map(async (swatch) => {
        const imageUrl = swatch.style.backgroundImage.replace(
          /url\(['"]?(.*?)['"]?\)/i,
          "$1"
        );
        const fallbackImage = swatch.dataset.fallbackImage;
        const actualColor = swatch.dataset.actualColor || "";
        const configImage = swatch.dataset.configImage || "";
        const titles = swatch.dataset.variantTitles || "";

        //  console.log("🥳🥳🥳😂😂😂", imageUrl, fallbackImage);

        if (!imageUrl && !fallbackImage) return;

        // First check if the image loads successfully
        const imageLoads = await new Promise((resolve) => {
          const testImg = new Image();
          testImg.crossOrigin = "Anonymous";
          testImg.src = imageUrl;

          testImg.onload = () => resolve(true);
          testImg.onerror = () => resolve(false);
        });

       

        if (imageLoads) return; // Image is valid - no action needed

        let firstValidImage = null;
        if (titles) {
          const cleanTitles = titles.replace(/"/g, "").replace(/\s*=\s*/g, "");

          const imgTitles = cleanTitles
            .split("|")
            .map((title) => title.trim())
            .filter((title) => title && title !== "");

          const formattedTitles = Array.isArray(imgTitles)
            ? imgTitles
                .map((title) =>
                  title && typeof title === "string"
                    ? title
                        .toLowerCase()
                        .replace(/[,/]/g, "") // remove ',' and '/'
                        .split(" ")
                        .join("-") // replace spaces with '-'
                        .replace(/-+/g, "-") // replace multiple '-' with single '-'
                    : ""
                )
                .filter((title) => title)
            : [];

          // Generate potential image URLs
          const potentialImageUrls = formattedTitles.map(
            (title) =>
              `//abchome.com/cdn/shop/files/${title
                .toLowerCase()
                .replace(/\s+/g, "-")}_64x64.png`
          );

          // Find the first valid image
          for (const url of potentialImageUrls) {
            if (await isImageValid(url)) {
              firstValidImage = url;
              break;
            }
          }
        }

        // Only proceed with fallback logic if image fails to load
        try {
          const actualColorValid = isColorValid(actualColor);
          const fallbackHex = await getHexColorFromImage(fallbackImage);

          // Customize this condition to reject unwanted colors
          const fallbackColorValid =
            fallbackHex.toLowerCase() !== "#ffffff" &&
            fallbackHex.toLowerCase() !== "#000000";

          if (fallbackColorValid) {
            // console.log("Step:", 1);
            if (actualColorValid) {
              //  console.log("Step:", 2);
              swatch.style.backgroundColor = actualColor;
              swatch.style.backgroundImage = firstValidImage
                ? `url(${firstValidImage})`
                : configImage;
            } else {
              //  console.log("Step:", 3)
              swatch.style.backgroundImage = "none";
              swatch.style.backgroundColor = fallbackHex;
            }
          } else {
            //  console.log("Step:", 4)
            swatch.style.backgroundImage = `url(${fallbackImage})`;
          }
        } catch (error) {
          //  console.log("Step:", 5)
          console.error("Error processing swatch:", error);
          swatch.style.backgroundImage = `url(${fallbackImage})`;
        }
      })
    );
  },

  // Generate swatches HTML
  generateSwatchesHTML: function (variantData, product, variantId) {
    const { options, map, images, swatchImages, colors, urls, titles } =
      variantData;
    const isRug = this.isRugProduct(product);
    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;
    // const isCylindo = product.mfield_custom_cylindo_code;
    const sidebarButton = document.querySelector(".filter-toggle-btn");
    const isSidebarActive =
      sidebarButton && sidebarButton.className.includes("active");

    const wrapperDataset = this.currentWrapperData;
    const isShowSwatch = this.getSwatchStatus(wrapperDataset);

    const isFurniture = Object.values(this.currentWrapperData)?.at(0)?.collectionHandle === "furniture" || product.template_suffix === "furniture_new"

    if (
      options.length <= 1 ||
      isRug ||
      isCustomRugFlow ||
      // isCylindo ||
      !isShowSwatch
    ) {
      return "";
    }

    const swatchesData =
      this.parseHtmlEncodedJsonString(product.ss_swatches) || [];

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const swatchCount = isMobile ? 3 : 5;

    return `
      <div class="product-card__swatches" onmouseleave="window.utils.handleVariantSwatchLeave(event, this)" >
        ${options
          .slice(0, swatchCount)
          .map((variant, index) => {
            const swatch =
              swatchesData.find((s) =>
                variant.toLowerCase().includes(s.color.toLowerCase())
              ) || {};
            let colorCode =
              (() => {
                try {
                  return JSON.parse(colors?.[variant])[0];
                } catch {
                  return colors?.[variant];
                }
              })() || "";
            const imageUrl = isFurniture ? variantData?.images[variant] : swatchImages[variant] || "";
            const fallbackImage = variantData?.images[variant];
            const areFirstTwoSame = (() => {
              const entries = Object.values(titles);
              if (entries.length === 0) return true; // Empty object = trivially true
              const [first, second] = entries[0]; // reference values

              return entries.every(([a, b]) => a === first && b === second);
            })();

            const variantTitles = areFirstTwoSame ? [] : titles[variant] || [];

            const hasImageMoreThanThreeTimes = Object.values(variantData.swatchImages).some((img, _, arr) => arr.filter(i => i === img).length > 3);

            return `
<span 
  class="new-swatch ${isSidebarActive ? "active" : ""}" 
  style="background-color: ${colorCode}; background-image: url('${hasImageMoreThanThreeTimes ? "" : swatchImages?.[variant]}'); background-size: cover; background-position: center; background-repeat: no-repeat;" 
  data-variant="${variant}"
  data-variant-id="${map[variant] || variantId}"
  data-image="${swatch.image || imageUrl}"
  data-hover-image="${swatch.image || imageUrl}"
  data-url="${urls[variant] || ""}"
  data-fallback-image="${fallbackImage}"
  data-actual-color="${colorCode}"
  data-config-image="${imageUrl || ""}"
  data-variant-titles="${(variantTitles || [])
    .map((title) => (title ? title.replace(/"/g, "&quot;") : ""))
    .join("|")}"
  onmouseenter="window.utils.handleVariantSwatchHover(event, this)"
  onclick="window.utils.handleVariantSwatchClick(event, this)">
</span>
          `;
          })
          .join("")}
        ${
          options.length > swatchCount
            ? `
          <div style="position: relative;">
            <div style="width: 23px; height: 23px; overflow: hidden; border-radius: 50%; position: relative; visibility: hidden;">
              <span style="background-image:url(${
                product.thumbnailImageUrl || ""
              }); background-size: cover; background-position: center; background-repeat: no-repeat;" class=" swatch--more"></span>
            </div>
            <span class="swatch_more_text">+${
              options.length - swatchCount 
            } Options</span>
          </div>
        `
            : ""
        }
      </div>
    `;
  },

  // Generate customize button HTML
  generateCustomizeButton: function (product) {
    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;
    const isCylindo = product.mfield_custom_cylindo_code;

    if (!isCustomRugFlow) {
      return "";
    }
    const wrapper = document.querySelector("body");

    if (wrapper.getAttribute("handle-template") == "furniture_2024") {
      return "";
    }
    return `
      <div class="customize-button" ${isCylindo ? 'data-cylindo="true"' : ""}>
        <span class="customize-icon">
          ${
            isCustomRugFlow
              ? `<svg class="maximize-icon" xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ff5f8b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <polyline points="17 3 21 3 21 7"></polyline>
  <line x1="21" y1="3" x2="14" y2="10"></line>
  <polyline points="7 21 3 21 3 16"></polyline>
  <line x1="3" y1="21" x2="10" y2="14"></line>
  <polyline points="3 8 3 3 8 3"></polyline>
  <line x1="3" y1="3" x2="10" y2="10"></line>
  <polyline points="21 16 21 21 16 21"></polyline>
  <line x1="21" y1="21" x2="14" y2="14"></line>
</svg>
          `
              : `
            <svg style="transform: rotate(-76deg);" class="color-wheel-icon" width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(60,60)">
                <defs>
                  <mask id="donut-mask">
                    <rect x="-60" y="-60" width="120" height="120" fill="white"/>
                    <circle cx="0" cy="0" r="20" fill="black"/>
                  </mask>
                </defs>
                <g mask="url(#donut-mask)">
                 <path d="M0,0 L0,-50 A50,50 0 0,1 38.27,-38.27 Z" fill="#F980A1"/> <!-- Pink -->
                <path d="M0,0 L38.27,-38.27 A50,50 0 0,1 50,0 Z" fill="#F44336"/> <!-- Red -->
                <path d="M0,0 L50,0 A50,50 0 0,1 38.27,38.27 Z" fill="#F9A825"/> <!-- Orange -->
                <path d="M0,0 L38.27,38.27 A50,50 0 0,1 0,50 Z" fill="#FDD180"/> <!-- Peach -->
                <path d="M0,0 L0,50 A50,50 0 0,1 -38.27,38.27 Z" fill="#8BC34A"/> <!-- Light Green -->
                <path d="M0,0 L-38.27,38.27 A50,50 0 0,1 -50,0 Z" fill="#4CAF50"/> <!-- Green -->
                <path d="M0,0 L-50,0 A50,50 0 0,1 -38.27,-38.27 Z" fill="#4B7C83"/> <!-- Teal -->
                <path d="M0,0 L-38.27,-38.27 A50,50 0 0,1 0,-50 Z" fill="#B3DCE3"/> <!-- Light Blue -->
                </g>
              </g>
            </svg>
          `
          }
        </span>
        <span class="customize-text">Customize your selection</span>
      </div>
    `;
  },

  generateSizeForFurniture: function (product) {
    const wrapper = document.querySelector("body");

    if (
      (!wrapper ||
      wrapper.getAttribute("handle-template") !== "furniture_2024") && product.template_suffix !== "furniture_new"
    ) {
      return "";
    }

    const sizesRaw = product.variant_size || "";
    const sizes = sizesRaw.includes("|")
      ? sizesRaw.split("|")
      : [sizesRaw.trim()];

    if (sizes.length <= 1) {
      return "";
    }

    return `
      <div class="customize-button">
        <span class="customize-text">Multiple Sizes Available</span>
      </div>
    `;
  },

  // Generate size text
  generateSizeText: function (product) {
    const sizesRaw = product.variant_size || "";
    const sizes = sizesRaw.includes("|")
      ? sizesRaw.split("|")
      : [sizesRaw.trim()];

    let sizeText =
      sizes.length > 1
        ? "Multiple sizes available"
        : this.extractSizeFromTitle(product.name) || sizes[0];

    return sizeText ? `<div class="product-card__size">${sizeText}</div>` : "";
  },

  generateProductCard: function (product) {
    const isCustomRugFlow = product.mfield_custom_custom_rug_flow;
    const isSlicedProduct = product.mfield_new_my_fields_product_slice;

    // console.log(isCustomRugFlow && JSON.parse(product?.mfield_custom_custom_rug_flow.replace(/&quot;/g, '"')));
    // console.log(isCustomRugFlow && product);

    // If it's a custom rug flow product, generate cards for each variant

    if ((isCustomRugFlow || isSlicedProduct === "true") && product?.ss_swatches) {
      return this.generateCustomRugFlowCards(product);
    }

    // Otherwise generate the regular product card
    return this.generateRegularProductCard(product);
  },

  // Generate regular product card
  generateRegularProductCard: function (product) {
    const title = product.name.replace(/\s?-\s?\d.*$/, "").trim();
    const productId = product.variant_product_id;
    const variantId = product.ss_id;
    const baseUrl = product.url || "#";
    const productHandle = product.handle;
    const varinatid = product.uid;
    const isRug = this.isRugProduct(product);
    const productPrice = product.variant_price;
    const wrapperDataset = this.currentWrapperData;
    const aspectRatio = this.getAspectRatioFromAnyDataset(wrapperDataset);
    const isPriceUponRequest =
      product?.template_suffix === "rugs_price-upon-request";
    const variants = this.parseHtmlEncodedJsonString(product?.variants?.[0]);

    // console.log("product", product, "variants", variants);

    // Process variant data
    const variantData = this.processVariants(product, variantId, baseUrl);

    // console.log(variantData);

    const parsePrice = (priceStr) => {
      if (!priceStr || priceStr.trim() === "") return null;
      const cleaned = priceStr.trim().replace(/\.00$/, "");
      const parsed = Number(cleaned.replace(/[^0-9.]/g, ""));
      return isNaN(parsed) ? null : parsed;
    };

    const originalPrice = parsePrice(product.msrp);
    const currentPrice = parsePrice(product.price);

    const toNumber = (value) => {
      if (!value) return 0;
      const num = parseFloat(value);
      return num > 1000 ? num / 100 : num;
    };

    const isOnSaleAndAvailable =
      variants.length > 1
        ? variants.some(
            (v) =>
              v.inventory_quantity > 0 &&
              toNumber(v.compare_at_price) > toNumber(v.price)
          ) || currentPrice < originalPrice
        : currentPrice < originalPrice;

    // Promo badge config from wrapper dataset
    const wrapperDataAny = Object.values(wrapperDataset)?.[0] || {};
    const showPromoBadge = String(wrapperDataAny?.showPromoBadge).toLowerCase() === "true";
    const promoBadgeText = wrapperDataAny?.promoBadgeText || "Buy More, Save Moreeee";
    const promoBadgeBg = wrapperDataAny?.promoBadgeBackgroundColor || "#FFFFFF";
    const promoBadgeColor = wrapperDataAny?.promoBadgeColor || "#8F88EE";
    const promoBadgeCollection = wrapperDataAny?.promoBadgeCollection;

    // Normalize promo collection ids from wrapper dataset (handles JSON string, array, or CSV)
    const promoCollectionIds = (() => {
      if (!promoBadgeCollection) return [];
      if (Array.isArray(promoBadgeCollection)) {
        return promoBadgeCollection.map((x) => String(x).trim()).filter(Boolean);
      }
      let raw = String(promoBadgeCollection).trim();
      try {
        if (/^\s*\[/.test(raw)) {
          const parsed = JSON.parse(raw);
          return (Array.isArray(parsed) ? parsed : []).map((x) => String(x).trim()).filter(Boolean);
        }
      } catch (e) {
        // fall through to CSV parsing
      }
      raw = raw.replace(/^[\[\(\{\s]+|[\]\)\}\s]+$/g, "");
      return raw
        .split(",")
        .map((s) => s.replace(/["']/g, "").trim())
        .filter(Boolean);
    })();

    // Normalize product collection ids from Searchspring product
    const productCollectionIds = Array.isArray(product?.collection_id)
      ? product.collection_id.map((x) => String(x).trim()).filter(Boolean)
      : (product?.collection_id ? [String(product.collection_id).trim()] : []);

    const isPromoEligible = showPromoBadge && promoCollectionIds.length > 0
      ? productCollectionIds.some((id) => promoCollectionIds.includes(id))
      : false;

    // Show promo badge if eligible, or "excluded from sale" if promo is active but product is not eligible
    const promoBadgeHtml = isPromoEligible
      ? `<div class="product-badges product-card__badges product-badges--promo"><span class="product-badge product-badge--promo" style="\n        --color-text: ${promoBadgeColor};\n        --color-bg: ${promoBadgeBg};\n      ">${promoBadgeText}</span></div>`
      : "";

    return `
      <div class="main-product-card" data-product-id="${productId}" data-product-price="${productPrice}">
        <div class="wishlist-button">
          <button
            class="wk-button wk-floating"
            data-product-handle="${productHandle}"
            data-variant-id="${varinatid}"
            type="button"
          >
            <span class="wk-icon">
              <svg viewBox="0 0 64 64">
                <path d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"><path>
              </svg>
            </span>
            <span class="wk-text">Add to Wishlist</span>
          </button>
        </div>
        <a href="${
          product?.url
        }" class="product-card__link" data-base-url="${baseUrl}">
          <div style="aspect-ratio: ${aspectRatio};" class="product-card__imagenew">
            <img style="aspect-ratio: ${aspectRatio};" data-hover-image="${
      product.ss_image_hover ||
      product.thumbnailImageUrl ||
      "//via.placeholder.com/300"
    }" src="${product.thumbnailImageUrl || "//via.placeholder.com/300"}"
                 alt="${product.name || "Product image"}"
                 loading="lazy">
            ${
              isOnSaleAndAvailable
                ? `<span class="product-card__badge">SALE</span>`
                : ""
            }
            ${promoBadgeHtml}
          </div>

          <div class="product-card__content">
            <div class="product-card__brand">${
              product.mfield_new_my_fields_brand_name || product.brand || ""
            }</div>
            <h3 class="product-card__title">${title || "Product title"}</h3>
             ${this.generateSizeForFurniture(product)}
            ${isRug ? this.generateSizeText(product) : ""}
            ${
              isPriceUponRequest
                ? `<div class="product-card__cta product-card__cta--contact">
                     Price Upon Request
                   </div>`
                : `<div class="product-card__price">
                     ${this.generatePriceHTML(product)}
                   </div>`
            }
            ${this.generateCustomizeButton(product)}
           
            ${this.generateSwatchesHTML(variantData, product, variantId)}
          </div>
        </a>
      </div>
    `;
  },

  // Generate multiple cards for custom rug flow products
  generateCustomRugFlowCards: function (product) {
    const variantData = this.processVariants(
      product,
      product.ss_id,
      product.url,
      (processSwatchImages = true)
    );
    const cards = [];

    // Generate cards for each variant
    variantData.options.forEach((variant, index) => {
      // Create a copy of the product with variant-specific data
      const variantProduct = { ...product };

      // Update product details for this variant
      variantProduct.name = `${product.name
        .replace(/\s?-\s?\d.*$/, "")
        .trim()} - ${variant}`;
      variantProduct.ss_id = variantData.map[variant];
      variantProduct.thumbnailImageUrl =
        variantData.images[variant] || product.thumbnailImageUrl;
      variantProduct.ss_image_hover =
        variantData.images[variant] || product.ss_image_hover;

      // Get variant-specific price if available
      if (product.variant_price) {
        const prices = product.variant_price.split("|");
        if (prices[index]) {
          variantProduct.price = prices[index].trim();
        }
      }

      // Update variant ID for wishlist button
      if (product.variant_id) {
        const variantIds = product.variant_id.split("|");
        if (variantIds[index]) {
          variantProduct.uid = variantIds[index].trim();
        }
      }

      if (variantData?.urls?.[variant]) {
        variantProduct.url = variantData.urls[variant];
      }

      // Generate card for this variant
      cards.push(this.generateRegularProductCard(variantProduct));
    });

    return cards.join("");
  },

  processVariants: function (product, variantId, baseUrl, processSwatchImages) {
    const filterVariants = this.parseHtmlEncodedJsonString(product?.variants?.[0]);
    const variants = filterVariants.filter(item => item.image);
    const parsedSwatches =
      this.parseHtmlEncodedJsonString(product.ss_swatches) || [];
    const swatchesData = parsedSwatches.filter((item) => item.available) || [];
    const colorsMetaFieldData = product.variant_mfield_my_fields_color;    

    // product.name === "Sonoma Platform Bed" && console.log("variants", variants);
    const hasDuplicateOption1 = variants.some((v, _, arr) => arr.filter(x => x.option1 === v.option1).length > 3);

    const isFurniture = Object.values(this.currentWrapperData)?.at(0)?.collectionHandle === "furniture" || product.template_suffix === "furniture_new"

    let testvariantOptions = [];
    let testvariantMap = {};
    let testvariantImages = {};
    let testvariantSwatchImages = {};
    let testvariantColors = {};
    let testvariantUrls = {};
    let testVariantTitles = {};

    let tvariantOptions = [];
    let tvariantMap = {};
    let tvariantImages = {};
    let tvariantSwatchImages = {};
    let tvariantColors = {};
    let tvariantUrls = {};
    let tVariantTitles = {};

    const viceVersaCompare = (a, b) => {
      const aTitle = a.toLowerCase();
      const bTitle = b.toLowerCase();

      return aTitle.includes(bTitle) || bTitle.includes(aTitle);
    };

    if (swatchesData.length > 0) {
      swatchesData.forEach((swatch) => {
        const swatchTitle = swatch.color;
        const variant =
          variants
            .filter((item) => item.mfield_new_my_fields_filter_color)
            .find((variant) => {
              const compare = viceVersaCompare(variant.title, swatchTitle);
              return compare;
            }) || {};

        const formatterTitle = (title) =>
          title ? title.toLowerCase().split(" ").join("-") : "";
        const colorData = variant?.mfield_new_my_fields_filter_color?.replace(
          /&quot;/g,
          '"'
        );
        const variantTitle =
          formatterTitle(variant?.option1) ||
          formatterTitle(variant?.option2) ||
          formatterTitle(variant?.title) ||
          formatterTitle(swatch?.color);

        testvariantOptions.push(swatchTitle);
        testvariantMap[swatchTitle] = swatchTitle;
        testvariantImages[swatchTitle] = swatch.image;
        testvariantSwatchImages[
          swatchTitle
        ] = variant?.mfield_custom_swatch || `//abchome.com/cdn/shop/files/${variantTitle
          .toLowerCase()
          .replace(/\s+/g, "-")}_64x64.png`;
        testvariantColors[swatchTitle] = colorData;
        testvariantUrls[swatchTitle] =
          swatch.url || `${baseUrl.split("?")[0]}?variant=${variant.id}`;
        testVariantTitles[swatchTitle] = [
          variant?.option1,
          variant?.option2,
          variant?.title,
          swatch?.color,
        ];
      });
    }

    if (colorsMetaFieldData?.length > 0) {
      colorsMetaFieldData.forEach((swatch) => {
        const swatchTitle = swatch;
        const variant =
          variants
          .find((item) => item.mfield_my_fields_color === swatchTitle) || {};

        if(!variant?.inventory_quantity) return;

        const formatterTitle = (title) =>
          title ? title.toLowerCase().split(" ").join("-") : "";
        const colorData = variant?.mfield_my_fields_color?.replace(
          /&quot;/g,
          '"'
        );
        
        const variantTitle =
          formatterTitle(hasDuplicateOption1 ? variant?.option2 : variant?.option1) ||
          formatterTitle(variant?.option2) ||
          formatterTitle(variant?.title) ||
          formatterTitle(swatch?.color);

        const cleanedVariantTitle = variantTitle.replace(/[,/]/g, "").replace(/--+/g, "-");

        tvariantOptions.push(swatchTitle);
        tvariantMap[swatchTitle] = swatchTitle;
        tvariantImages[swatchTitle] = variant.image;
        tvariantSwatchImages[
          swatchTitle
         ] = variant?.mfield_custom_swatch || `//abchome.com/cdn/shop/files/${cleanedVariantTitle
          .toLowerCase()
          .replace(/\s+/g, "-")}_64x64.png`;
        tvariantColors[swatchTitle] = colorData;
        tvariantUrls[swatchTitle] =
          swatch.url || `${baseUrl.split("?")[0]}?variant=${variant.id}`;
        tVariantTitles[swatchTitle] = [
          variant?.option1,
          variant?.option2,
          variant?.title,
          swatch?.color,
        ];
      });
    }

    if(isFurniture) {
      return {
        options: tvariantOptions,
        map: tvariantMap,
        images: tvariantImages,
        colors: tvariantColors,
        urls: tvariantUrls,
        swatchImages: tvariantSwatchImages,
        titles: tVariantTitles,
      }
    } else {
      return {
        options: testvariantOptions,
        map: testvariantMap,
        images: testvariantImages,
        colors: testvariantColors,
        urls: testvariantUrls,
        swatchImages: testvariantSwatchImages,
        titles: testVariantTitles,
      }
    }
  },

  handleVariantSwatchHover: (event, element) => {
    // Don't apply hover effects on mobile
    if (window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const variantId = element.getAttribute("data-variant-id");
    const productCard = element.closest(".main-product-card");
    const link = productCard.querySelector(".product-card__link");
    const image = productCard.querySelector(".product-card__imagenew img");

    // Store original image if not already stored
    if (!image.hasAttribute("data-original-src")) {
      image.setAttribute("data-original-src", image.getAttribute("src"));
    }

    // Get the URL from the swatch data
    const variantUrl = element.getAttribute("data-url");
    if (variantUrl) {
      link.setAttribute("href", variantUrl);
    } else {
      const baseUrl = link.getAttribute("data-base-url");
      const newUrl = `${baseUrl.split("?")[0]}?variant=${variantId}`;
      link.setAttribute("href", newUrl);
    }

    // Update the product image on hover
    const hoverImage =
      element.getAttribute("data-hover-image") ||
      element.getAttribute("data-image");
    if (hoverImage && hoverImage !== "undefined") {
      image.setAttribute("src", hoverImage);
    }

    // Highlight the hovered swatch
    productCard.querySelectorAll(".new-swatch").forEach((swatch) => {
      swatch.classList.remove("swatch--active");
    });
    element.classList.add("swatch--active");
  },

  handleVariantSwatchLeave: (event, element) => {
    // Only apply leave effects on desktop
    if (window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    const productCard = element.closest(".main-product-card");
    const image = productCard.querySelector(".product-card__imagenew img");
    const originalSrc = image.getAttribute("data-original-src");

    if (originalSrc) {
      image.setAttribute("src", originalSrc);
    }

    // Optional: Reset the active swatch to the first one
    const swatches = productCard.querySelectorAll(".new-swatch");
    if (swatches.length > 0) {
      swatches.forEach((swatch) => swatch.classList.remove("swatch--active"));
      // swatches[0].classList.add("swatch--active");
    }
  },

  handleVariantSwatchClick: (event, element) => {
    // Only apply click effects on mobile
    if (!window.matchMedia("(max-width: 768px)").matches) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const variantId = element.getAttribute("data-variant-id");
    const productCard = element.closest(".main-product-card");
    const link = productCard.querySelector(".product-card__link");
    const image = productCard.querySelector(".product-card__imagenew img");

    // Store original image if not already stored
    if (!image.hasAttribute("data-original-src")) {
      image.setAttribute("data-original-src", image.getAttribute("src"));
    }

    // Get the URL from the swatch data
    const variantUrl = element.getAttribute("data-url");
    if (variantUrl) {
      link.setAttribute("href", variantUrl);
    } else {
      const baseUrl = link.getAttribute("data-base-url");
      const newUrl = `${baseUrl.split("?")[0]}?variant=${variantId}`;
      link.setAttribute("href", newUrl);
    }

    // Update the product image on click
    const hoverImage =
      element.getAttribute("data-hover-image") ||
      element.getAttribute("data-image");
    if (hoverImage) {
      image.setAttribute("src", hoverImage);
    }

    // Highlight the clicked swatch
    productCard.querySelectorAll(".new-swatch").forEach((swatch) => {
      swatch.classList.remove("swatch--active");
    });
    element.classList.add("swatch--active");
  },

  parseHtmlEncodedJsonString: (encoded) => {
    try {
      // Step 1: Decode HTML entities (&quot; → ", etc.)
      const htmlDecoded = encoded
        .replace(/&quot;/g, '"')
        .replace(/\\&quot;/g, '"') // inner escaped quotes like 35.5\" → 35.5"
        .replace(/\\\\/g, "\\"); // reduce double backslashes if any

      // Step 2: Parse into JS object/array
      return JSON.parse(htmlDecoded);
    } catch (e) {
      // console.error("❌ Failed to parse JSON:", e.message);
      return [];
    }
  },

  getMarketingBlocks(sectionId) {
    if (!sectionId) return [];

    if (!window.utils.parsedMarketingBlocks[sectionId]) {
      const raw =
        window.utils.currentWrapperData[sectionId]?.marketingBlocks || "[]";
      const parsed = window.utils.parseHtmlEncodedJsonString(raw);
      window.utils.parsedMarketingBlocks[sectionId] = Array.isArray(parsed)
        ? parsed
        : [];
    }

    return window.utils.parsedMarketingBlocks[sectionId];
  },

  indexMarketingBlocksByPosition(blocks = []) {
    return (blocks || []).reduce((acc, block) => {
      const position = Number(block?.settings?.position);
      if (!Number.isFinite(position)) {
        return acc;
      }
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(block);
      return acc;
    }, {});
  },

  getPageStartIndex(pagination, productsPerPage, fallbackPage = 1) {
    if (typeof pagination?.start === "number") {
      return pagination.start;
    }

    const currentPage =
      Number(pagination?.currentPage) ||
      Number(pagination?.page) ||
      Number(fallbackPage) ||
      1;

    const pageSize =
      Number(pagination?.resultsPerPage) ||
      Number(pagination?.pageSize) ||
      Number(productsPerPage) ||
      0;

    return Math.max(0, (currentPage - 1) * pageSize);
  },

  composeProductsWithMarketingBlocks({
    results = [],
    marketingBlockMap = {},
    startIndex = 0,
  }) {
    const fragments = [];
    let absoluteIndex = startIndex;

    results.forEach((product) => {
      const blocks = marketingBlockMap[absoluteIndex];
      if (blocks && blocks.length) {
        blocks.forEach((block) => {
          fragments.push(window.utils.generateMarketingBlock(block));
        });
      }

      fragments.push(window.utils.generateProductCard(product));
      absoluteIndex += 1;
    });

    return fragments.join("");
  },

  renderPagination: (paginationContainer, pagination, loadProducts) => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    let paginationHTML = `
      <button ${currentPage === 1 ? "disabled" : ""} data-page="${
      currentPage - 1
    }">
        &larr; Previous
      </button>
      <span>Page ${currentPage} of ${totalPages}</span>
      <button ${currentPage >= totalPages ? "disabled" : ""} data-page="${
      currentPage + 1
    }">
        Next &rarr;
      </button>
    `;

    paginationContainer.innerHTML = paginationHTML;

    // Add event listeners to pagination buttons
    paginationContainer.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", () => {
        const page = parseInt(button.dataset.page);
        if (!isNaN(page)) loadProducts(page);
      });
    });
  },

  populateSortingOptions: (sortingData) => {
    const dropdownOptions = document.querySelector(".dropdown-options");

    if (dropdownOptions) {
      dropdownOptions.innerHTML = "";
    }

    if (sortingData?.options?.length) {
      sortingData.options.forEach((option) => {
        const li = document.createElement("li");

        li.className = "sort__option";
        li.dataset.value = `${option.field}_${option.direction}`;
        li.textContent = option.label;

        dropdownOptions && dropdownOptions.appendChild(li);
      });
    }

    if (dropdownOptions?.firstChild) {
      dropdownOptions.firstChild.classList.add("active");
    }
  },

  loadProducts: async ({
    page = 1,
    baseUrl,
    productsPerPage,
    productGrid,
    paginationContainer,
    showLoading,
    hideLoading,
    callback = () => {},
    totalCountElement,
    filterParams,
    source,
    sectionId,
    updateMode = "replace",
  }) => {
    if (source === "filter-apply") {
      document.querySelector(".product-grid").style.visibility = "hidden";
    }
    showLoading();

    const options = {
      method: "GET",
      headers: { accept: "application/json" },
    };
    try {
      const isBestSelling =
        window.utils.sortParam === "sort.ga_unique_purchases=desc"
        ? "&sort.ga_unique_purchases=desc"
        : "";
      const apiUrl = `${baseUrl}&page=${page}&resultsPerPage=${productsPerPage}${isBestSelling}`;
      const response = await fetch(apiUrl, options);
      const urlSortParam = (apiUrl.match(/sort\.[^=]+=[^&]+/) || [null])[0];
      window.utils.sortParam = urlSortParam;
      const data = await response.json();

      const marketingBlocks = utils.getMarketingBlocks(sectionId);
      const marketingBlockMap =
        utils.indexMarketingBlocksByPosition(marketingBlocks);
      const startIndex = utils.getPageStartIndex(
        data.pagination,
        productsPerPage,
        page
      );

      if (data?.sorting?.options.length) {
        utils.populateSortingOptions(data?.sorting);
      }

      if (data.results?.length) {
        const gridHtml = utils.composeProductsWithMarketingBlocks({
          results: data.results,
          marketingBlockMap,
          startIndex,
        });

        if (source === "filter-apply") {
          document.querySelector(".product-grid").style.visibility = "visible";
        }

        if (updateMode === "append") {
          productGrid.insertAdjacentHTML("beforeend", gridHtml);
        } else {
          productGrid.innerHTML = gridHtml;
        }

        setTimeout(() => {
          utils.validateSwatchImages();
        }, 100);

        utils.paginationData = data.pagination;

        if (paginationContainer) {
          utils.renderPagination(
            paginationContainer,
            data.pagination,
            (newPage) => {
              utils.loadProducts({
                page: newPage,
                baseUrl,
                productsPerPage,
                productGrid,
                paginationContainer,
                showLoading,
                hideLoading,
                callback,
                totalCountElement: "",
                filterParams: "",
                marketingBlocks,
                sectionId,
              });
            }
          );
        }

        if (totalCountElement) {
          totalCountElement.style.display = "block";
          totalCountElement.textContent = `${data.pagination.totalResults} Items`;
        }

        callback(data);
      } else {
        productGrid.innerHTML =
          '<p class="no-products">No products found in this collection.</p>';
        if (paginationContainer) paginationContainer.innerHTML = "";
      }

      // Update URL without reload
      // const url = new URL(window.location);
      // url.searchParams.set("page", page);
      // window.history.replaceState({}, "", url);
    } catch (error) {
      console.error("Fetch error:", error);
      productGrid.innerHTML =
        '<p class="error-message">Error loading products. Please try again.</p>';
    } finally {
      hideLoading();
    }
  },

  setupSorting: function (options) {
    const {
      sortSelector,
      baseUrl,
      siteId,
      collectionId,
      collectionHandle,
      paginationType,
      productGrid,
      paginationContainer,
      productsPerPage,
      showLoading,
      hideLoading,
      loadProducts,
      loadingOverlay,
      infiniteScrollingLoading,
      totalCountElement,
      sectionId,
    } = options;

    if (!sortSelector) return;

    const defaultSelected =
      sortSelector.querySelector(".dropdown-selected")?.textContent;

    // if(defaultSelected === "Featured"){
    //   window.utils.sortParam = "sort.ga_unique_purchases=desc";
    // }

    const dropdownOptions = sortSelector.querySelector(".dropdown-options");

    function toggleChevronDisplay(container) {
      const chevronDown = container.querySelector(".chevron-down");
      const chevronUp = container.querySelector(".chevron-up");

      const isDownVisible = chevronDown.style.display !== "none";
      chevronDown.style.display = isDownVisible ? "none" : "inline";
      chevronUp.style.display = isDownVisible ? "inline" : "none";
    }

    const toggleDropdown = () => {
      if (dropdownOptions.style.visibility === "visible") {
        dropdownOptions.style.visibility = "hidden";
        toggleChevronDisplay(sortSelector);
      } else {
        dropdownOptions.style.visibility = "visible";
        toggleChevronDisplay(sortSelector);
      }
    };

    sortSelector.addEventListener("click", () => {
      toggleDropdown();
    });

    const closeOnClickOutside = (e) => {
      if (!sortSelector.contains(e.target)) {
        const chevronDown = sortSelector.querySelector(".chevron-down");
        const chevronUp = sortSelector.querySelector(".chevron-up");

        dropdownOptions.style.visibility = "hidden";
        chevronDown.style.display = "inline";
        chevronUp.style.display = "none";

        document.removeEventListener("click", closeOnClickOutside);
      }
    };

    // Handle selected item click
    sortSelector.addEventListener("click", (e) => {
      e.stopPropagation();

      document.addEventListener("click", closeOnClickOutside);
    });

    // Handle click on list items
    sortSelector
      .querySelector(".dropdown-options")
      .addEventListener("click", (e) => {
        const listItem = e.target.closest("li");
        if (!listItem || !listItem.dataset.value) return;

        const selectedText = listItem.textContent;
        sortSelector.querySelector(".dropdown-selected").textContent =
          selectedText;

        const sortValue = listItem.dataset.value;

        // ! need to map with search spring sort params
        switch (sortValue) {
          case "ga_unique_purchases_desc":
            window.utils.sortParam = "";
            break;
          case "featured":
            window.utils.sortParam = "sort.ss_boost=desc";
            break;
          case "ss_price_asc":
            window.utils.sortParam = "sort.ss_price=asc";
            break;
          case "ss_price_desc":
            window.utils.sortParam = "sort.ss_price=desc";
            break;
          case "ss_pct_off_desc":
            window.utils.sortParam = "sort.ss_pct_off=desc";
            break;
          case "variant_mfield_custom_item_revenue_asc":
            window.utils.sortParam =
              "sort.variant_mfield_custom_item_revenue=asc";
            break;
          case "ss_variant_mfield_custom_units_purchased_asc":
            window.utils.sortParam =
              "sort.ss_variant_mfield_custom_units_purchased=asc";
            break;
          default:
            window.utils.sortParam = "";
        }

        // Update active class
        Array.from(sortSelector.querySelectorAll("li")).forEach((li) => {
          li.classList.remove("active");
        });
        listItem.classList.add("active");

        // Persist sort selection in sessionStorage along with any existing filter data, keyed by collection
        const storedState = utils.loadSavedFilters(collectionHandle) || {
          filters: {},
          handle: collectionHandle,
        };
        storedState.sortParam = window.utils.sortParam;
        storedState.handle = collectionHandle;
        sessionStorage.setItem("plpActiveFilters", JSON.stringify(storedState));

        const globalUrl = window.utils.globalUrl(
          baseUrl,
          (page = 1),
          productsPerPage
        );
        const url = `${globalUrl}${window.utils.filterParams}${
          window.utils.sortParam && `&${window.utils.sortParam}`
        }`;

        loadProducts({
          page: 1,
          baseUrl: url,
          productsPerPage,
          productGrid,
          paginationContainer,
          showLoading,
          hideLoading,
          callback: () => {},
          totalCountElement,
          filterParams: window.utils.filterParams,
          sectionId,
        });

        const scrollToTop = () =>
          window.scrollTo({ top: 0, behavior: "instant" });
        scrollToTop();

        // Reflect current filters + sort in the URL
        (() => {
          const newUrl = new URL(
            window.location.pathname,
            window.location.origin
          );
          let search = "?page=1";
          if (window.utils.filterParams)
            search += `${window.utils.filterParams}`;
          if (window.utils.sortParam) search += `&${window.utils.sortParam}`;
          newUrl.search = search;
          window.history.pushState({}, "", newUrl);
        })();
      });
  },

  setupInfiniteScroll: ({
    baseUrl,
    productsPerPage,
    productGrid,
    loadingOverlay,
    infiniteScrollingLoading,
    loadProducts,
    totalCountElement,
    sortSelector,
    sectionId,
  }) => {
    let isLoading = false;
    let observer;
    const urlParams = new URLSearchParams(window.location.search);
    const pageNum = parseInt(urlParams.get("page")) || 1;

    const marketingBlocks = utils.getMarketingBlocks(sectionId);
    const marketingBlockMap =
      utils.indexMarketingBlocksByPosition(marketingBlocks);

    const showLoading = () => (loadingOverlay.style.display = "block");
    const hideLoading = () => (loadingOverlay.style.display = "none");

    const updateUrl = (page) => {
      const url = new URL(window.location.href);
      url.searchParams.set("page", page);
      window.history.pushState({}, "", url);
    };

    const createSkeletonCards = (count) => {
      const skeletonHtml = Array(count)
        .fill(
          `
      <div class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-text-line"></div>
        <div class="skeleton-text-line short"></div>
      </div>
    `).join('');
    
    const fragment = document.createDocumentFragment();
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = skeletonHtml;
    while (tempDiv.firstChild) {
      fragment.appendChild(tempDiv.firstChild);
    }
    return fragment;
  };

    const removeSkeletonCards = () => {
      const skeletonCards = productGrid.querySelectorAll(".skeleton-card");
      skeletonCards.forEach((card) => card.remove());
    };

    const loadMoreProducts = (page) => {
      isLoading = true;

      // Add skeleton cards (4 or productsPerPage, whichever is smaller)
      productGrid.appendChild(createSkeletonCards(productsPerPage));

      fetch(
        `${window.utils.globalUrl(baseUrl, page, productsPerPage)}${
          window.utils.filterParams
        }${
          window.utils.sortParam ? `&${window.utils.sortParam}` : ""
        }`
      )
        .then((response) => response.json())
        .then((data) => {
          updateUrl(page);
          if (data.results?.length) {
            // Remove skeleton cards before adding real products
            removeSkeletonCards();

            const startIndex = utils.getPageStartIndex(
              data.pagination,
              productsPerPage,
              page
            );
            const batchHtml = utils.composeProductsWithMarketingBlocks({
              results: data.results,
              marketingBlockMap,
              startIndex,
            });
            const fragment = document.createDocumentFragment();
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = batchHtml;
            while (tempDiv.firstChild) {
              fragment.appendChild(tempDiv.firstChild);
            }
            productGrid.appendChild(fragment);
            setTimeout(() => {
              window.utils.validateSwatchImages();
            }, 100);
            utils.paginationData.totalPages = data.pagination.totalPages;

            // NEW: refresh facets UI using the same response, no page refresh
            try {
              const filtersContainer = document.querySelector(
                ".searchspring-filters"
              );
              if (filtersContainer) {
                if (Array.isArray(data.facets)) {
                  utils.renderFilters(data.facets, filtersContainer);
                } else {
                  // fallback to fetch facets for current URL
                  const currentUrl = `${window.utils.globalUrl(
                    baseUrl,
                    page,
                    productsPerPage
                  )}${window.utils.filterParams}${
                    window.utils.sortParam ? `&${window.utils.sortParam}` : ""
                  }`;
                  utils
                    .loadFacets(currentUrl, null, null)
                    .then((facets) => {
                      utils.renderFilters(facets, filtersContainer);
                    })
                    .catch(() => {});
                }
              }
            } catch (_) {}
          }
        })
        .catch((error) => {
          console.error("Error loading more products:", error);
          // Remove skeleton cards on error too
          removeSkeletonCards();
        })
        .finally(() => {
          isLoading = false;
        });
    };

    const setupIntersectionObserver = () => {
      const sentinel = document.createElement("div");
      sentinel.id = "infinite-scroll-sentinel";
      sentinel.style.height = "1px";
      sentinel.style.width = "100%";
      document.body.appendChild(sentinel);

      observer = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !isLoading &&
            utils.paginationData.currentPage < utils.paginationData.totalPages
          ) {
            utils.paginationData.currentPage =
              utils.paginationData.currentPage + 1;
            loadMoreProducts(utils.paginationData.currentPage);
          }
        },
        {
          root: null,
          rootMargin: "1300px 0px", // preload 1300px before
          threshold: 0,
        }
      );

      observer.observe(sentinel);
    };

    // Initial load
    loadProducts({
      page: 1,
      baseUrl,
      productsPerPage,
      productGrid,
      paginationContainer: null,
      showLoading,
      hideLoading,
      callback: (data) => {
        totalPages = data.pagination.totalPages;
        setupIntersectionObserver();
      },
      totalCountElement,
      filterParams: "",
      sectionId,
    });
    // Cleanup method
    window.cleanupInfiniteScroll = () => {
      if (observer) observer.disconnect();
      const sentinel = document.getElementById("infinite-scroll-sentinel");
      if (sentinel) document.body.removeChild(sentinel);
    };
  },

  // Filter functionality
  renderFilters: (facets, filtersContainer) => {
    if (!facets || !filtersContainer) return;

    const filtersContent = filtersContainer.querySelector(
      ".searchspring-filters-content"
    );
    if (!filtersContent) return;

    let filtersHTML = "";

    // --- Dynamic grouping by label (preserve API order) ---
    const groups = new Map(); // label -> facets[]
    facets.forEach((facet) => {
      const label = facet.label || facet.field;
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(facet);
    });

    // Build HTML with custom ordering if provided via data-show-filter-names
    let orderedLabels = [];
    const wrapperEl = document.querySelector(".searchspring-wrapper");

    if (wrapperEl && wrapperEl.dataset.showFilterNames) {
      orderedLabels = wrapperEl.dataset.showFilterNames
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (wrapperEl && wrapperEl.dataset.showSearchPageFilterNames) {
      orderedLabels = wrapperEl.dataset.showSearchPageFilterNames
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    // Parse hidden filter labels (comma-separated) from wrapper attribute
    let hiddenFilterLabels = [];
    if (wrapperEl && wrapperEl.dataset.hiddenFilters) {
      hiddenFilterLabels = wrapperEl.dataset.hiddenFilters
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    const hiddenFiltersSet = new Set(hiddenFilterLabels);

    // Parse collapsed filter labels (comma-separated) from wrapper attribute
    let collapsedFilterLabels = [];
    if (wrapperEl && wrapperEl.dataset.collapsedFilters) {
      collapsedFilterLabels = wrapperEl.dataset.collapsedFilters
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    const collapsedFiltersSet = new Set(collapsedFilterLabels);

    const seen = new Set();
    const renderLabel = (label) => {
      const normalizedLabel = label.toLowerCase();
      if (hiddenFiltersSet.has(normalizedLabel)) return; // skip hidden filters
      if (!groups.has(label) || seen.has(label)) return;
      seen.add(label);
      const facetList = groups.get(label);
      const slug = label.toLowerCase().replace(/\s+/g, "-");
      const firstFacet = facetList[0];

      // === Hide filters with <= 1 option (except for certain fields) ===
      const exceptionFields = new Set([
        "ss_price", // price slider/range
        "variant_mfield_new_my_fields_filter_color", // color swatches
        // 'variant_mfield_new_my_fields_lead_time', // quickship consolidated
      ]);

      if (!exceptionFields.has(firstFacet.field)) {
        // Count values with non-zero count; fallback to total length if counts missing
        const totalOptions = facetList.reduce((sum, f) => {
          const valuesArr = f.values || [];
          const meaningful = valuesArr.filter((v) => (v.count ?? 1) > 0);
          return sum + meaningful.length;
        }, 0);
        // if (totalOptions <= 1) {
        //   return; // Skip rendering this filter group
        // }
      }

      // Special case: render Quickship filter directly without collapsible heading
      // if (label.toLowerCase() === 'quickship') {
      //   filtersHTML += `
      //     <div class="collection-sidebar__group quickship-group" data-filter-field="${firstFacet.field}" data-group="${slug.toLowerCase()}">
      //       ${facetList.map((facet) => utils.renderFilterOptions(facet, { label })).join('')}
      //     </div>`;
      //   return;
      // }

      const isMobileViewport = window.innerWidth <= 768;
      const isCollapsed = collapsedFiltersSet.has(normalizedLabel);
      const headingStateClass =
        isMobileViewport || isCollapsed ? "closed" : "open";
      const contentDisplayStyle =
        isMobileViewport || isCollapsed ? "none" : "block";

      filtersHTML += `
        <div class="collection-sidebar__group" data-filter-field="${
          firstFacet.field
        }" data-group="${slug.toLowerCase()}">
          <button type="button" class="collection-sidebar__heading ${headingStateClass} " data-heading="${label}">
            <span data-sidebar-title>${label}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10">\n  <path d="M.357 2.753l4.39 4.39a.357.357 0 0 0 .506 0l4.39-4.39" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1"></path>\n</svg>

          </button>
          <div class="collection-sidebar__content" style="display: ${contentDisplayStyle}; padding-bottom: 0px;">
            ${facetList
              .map((facet) => utils.renderFilterOptions(facet, { label }))
              .join("")}
          </div>
        </div>`;
    };

    // First render labels specified by merchant in the desired order
    orderedLabels.forEach((lbl) => {
      // Find exact or case-insensitive match in groups keys
      const matchKey = Array.from(groups.keys()).find(
        (g) => g.toLowerCase() === lbl.toLowerCase()
      );
      if (matchKey) renderLabel(matchKey);
    });

    // Render any remaining groups only when merchant has NOT provided an explicit list
    if (orderedLabels.length === 0) {
      groups.forEach((_v, lbl) => {
        if (!seen.has(lbl)) renderLabel(lbl);
      });
    }

    filtersContent.innerHTML = filtersHTML;

    // ! this is a hack to remove the desktop-only filters on mobile
    if (window.innerWidth <= 768) {
      document.querySelector(".desktop-only").innerHTML = "";
    }
  },

  renderFilterOptions: (facet, filterConfig) => {
    // For jewelry metal facet, exclude composite values containing a pipe ("|"), e.g., "Yellow Gold|Silver"
    const facetValues =
      facet.field === "variant_mfield_custom_jewelry_metal_filter"
        ? (facet.values || []).filter((v) => !String(v.value).includes("|"))
        : facet.values;

    // Quickship consolidated filter for lead time < 4 weeks
    // if (facet.field === "variant_mfield_new_my_fields_lead_time") {
    //   return utils.renderQuickshipFilter(facet);
    // }

    if (facet.field === "ss_price") {
      return utils.renderPriceFilter(facet);
    }

    if (facet.field === "variant_mfield_new_my_fields_filter_color") {
      return utils.renderColorFilter(facet);
    }

    if (facet.field === "variant_mfield_custom_rug_size_bucket_filter") {
      return utils.renderSizeFilter(facet);
    }

    if (facet.field === "variant_mfield_custom_width_filter") {
      return utils.renderWidthBucketFilter(facet);
    }

    // Width & Length rounded filters – use dropdown instead of slider
    if (
      facet.field === "mfield_custom_length_rounded_ft_filter" ||
      facet.field === "mfield_custom_width_rounded_ft_filter"
    ) {
      const dimType = facet.field.includes("width") ? "width" : "length";
      return utils.renderDropdownDimensionFilter(facet, dimType);
    }

    // === NEW: dedicated renderer for ss_all_width_filter (range buckets in inches) ===
    if (facet.field === "ss_all_width_filter") {
      return utils.renderWidthRangeFilter(facet);
    }

    // Standard checkbox filter using traditional Shopify filter-item structure
    const MAX_VISIBLE = 12;
    const itemsHTML = facetValues.map((value, idx) => {
      // Clean label for display

      let displayLabel = value.label != null ? value.label : value.value;
      // Ensure we are working with a string to avoid runtime errors
      if (typeof displayLabel !== "string") {
        try {
          displayLabel = JSON.stringify(displayLabel);
        } catch {
          displayLabel = String(displayLabel);
        }
      }
      let count = value.count;

      // Clean up label if it's in JSON format
      if (displayLabel.includes('["') || displayLabel.includes('"')) {
        try {
          const parsed = JSON.parse(displayLabel.replace(/\|/g, ","));
          displayLabel = Array.isArray(parsed) ? parsed.join(", ") : parsed;
        } catch (e) {
          displayLabel = displayLabel.replace(/\[|\]|"/g, "");
        }
      }

      // Clean up pipe-separated values for readability
      if (displayLabel.includes("|")) {
        displayLabel = displayLabel.split("|")[0];
      }

      // Clean up any category paths
      if (displayLabel.includes(">")) {
        displayLabel = displayLabel.split(">").pop().trim();
      }

      const isDisabled = count === 0 && !value.active;
      const inputId = `filter--${facet.field}--${displayLabel.replace(
        /[^a-zA-Z0-9]/g,
        ""
      )}-searchspring`;
      const hiddenClass = idx >= MAX_VISIBLE ? "filter-item--hidden" : "";

      return `
        <li class="filter-item filter-item-checkbox ${hiddenClass} ${
        isDisabled ? "filter-item--disabled" : ""
      }" data-type="checkbox" ${hiddenClass ? 'style="display:none;"' : ""}>
          <label 
            data-filter
            data-filter-item-label
            class="filter-item__content filter-item__content--checkbox no-transition ${
              isDisabled ? "filter-item__content--disabled" : ""
            }"
            for="${inputId}"
          >
            <input 
              type="checkbox" 
              name="${facet.field}"
              data-value-escaped="${encodeURIComponent(value.value)}"
              value="${value.value}"
              class="filter-item__input visually-hidden"
              id="${inputId}"
              data-field="${facet.field}"
              data-value="${value.value}"
              ${value.active ? "checked" : ""}
              ${isDisabled ? "disabled" : ""}
              data-filter="${facet.field}=${value.value}"
              data-filter-item
            >
            <div class="filter-item__checkbox">
              <svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <span class="filter-item__label type-body-regular">${displayLabel}</span>
            <span class="filter-item__count">${count}</span>
          </label>
        </li>`;
    });

    const needsShowMore = facetValues.length > MAX_VISIBLE;
    const showMoreHTML = needsShowMore
      ? `<li class="filter-show-more" ><a  class="reset-all show-more-btn" style="cursor: pointer;" data-state="collapsed">View more</a></li>`
      : "";

    return `
      <ul class="filter-drawer__list">
        ${itemsHTML.join("")}
        ${showMoreHTML}
      </ul>
    `;
  },

  renderColorFilter: (facet) => {
    // Map of color names to hex codes for fallback
    const colorMap = {
      Black: "#000000",
      Blue: "#1e5aaa",
      Brown: "#8B4513",
      Gold: "#D4AF37",
      Green: "#2e8a4a",
      Grey: "#AAAAAA",
      Multi: "linear-gradient(135deg, #ff5e62, #2980B9, #1abc9c, #FFDD00)",
      Natural: "#E5DCC5",
      Orange: "#FFA500",
      Pink: "#FF7B9C",
      Purple: "#800080",
      Red: "#E63946",
      Silver: "#C0C0C0",
      White: "#FFFFFF",
      Yellow: "#F7DC6F",
      Cream: "#FFFDD0",
      Beige: "#F5F5DC",
    };

    const colorOptions = facet.values.flatMap(({ value: raw, count }) => {
      // raw === '["White"]' or maybe '["Blue"]|["White"]'
      const parts = raw.split("|");
      const colors = [];

      for (const part of parts) {
        try {
          const arr = JSON.parse(part);
          if (Array.isArray(arr)) colors.push(...arr);
        } catch {
          const cleaned = part.replace(/[\[\]"]/g, "");
          if (cleaned) colors.push(cleaned);
        }
      }

      return colors.length ? [{ value: raw, color: colors[0], count }] : [];
    });

    // Dedupe by color, keeping highest count
    const unique = {};
    for (const opt of colorOptions) {
      const key = opt.color.toLowerCase();
      if (!unique[key] || unique[key].count < opt.count) {
        unique[key] = opt;
      }
    }

    // Render only the colors we actually saw, in the order of colorMap
    return `
		<div class="color-filters">
		  ${Object.keys(colorMap)
        .map((colorName) => {
          const opt = unique[colorName.toLowerCase()];
          if (!opt) return "";

          const fileName = colorName.toLowerCase().replace(/\s+/g, "");
          const bgImg = `url(//abchome.com/cdn/shop/files/${fileName}.png)`;
          const bgColor = colorMap[colorName];

          return `
				<div
				  class="filter-item__swatch"
				  style="background-image: ${bgImg}; background-color: ${bgColor};"
				  tabindex="0"
				  aria-label="${colorName}"
				  data-color="${colorName}"
				  data-field="${facet.field}"
				  data-value='${opt.value.replace(/[\[\]"]/g, "")}'
				></div>
			  `;
        })
        .join("")}
		</div>
	  `;
  },

  renderSizeFilter: (facet) => {
    const lookup = {};

    facet.values.forEach((facetValue) => {
      // Skip values that contain multiple sizes separated by a pipe (e.g., "5x8|8x10")
      if (String(facetValue.value).includes("|")) return;
      const parts = String(facetValue.value).split("|");
      parts.forEach((token) => {
        const size = token.trim();
        if (!size) return;
        if (!lookup[size]) {
          lookup[size] = { size, count: 0, values: new Set() };
        }
        lookup[size].count += facetValue.count;
        lookup[size].values.add(facetValue.value);
      });
    });

    const sizeOptions = Object.values(lookup).filter((o) => o.count > 0);

    // Sort sizes for a user-friendly order:
    //  1) Numeric sizes ascending by area (width*length) – e.g., 4x6 < 5x8
    //  2) Non-numeric labels (Runner, Round, Small, Oversized, etc.) alphabetically afterwards
    const parseArea = (s) => {
      const match = s.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/i);
      if (!match) return null;
      return parseFloat(match[1]) * parseFloat(match[2]);
    };

    sizeOptions.sort((a, b) => {
      const areaA = parseArea(a.size);
      const areaB = parseArea(b.size);

      if (areaA !== null && areaB !== null) {
        return areaA - areaB;
      }
      if (areaA !== null) return -1;
      if (areaB !== null) return 1;
      return a.size.localeCompare(b.size);
    });

    return `
      <ul class="filter-drawer__list">
        ${sizeOptions
          .map((opt) => {
            const joinedValues = Array.from(opt.values).join(",");
            const isDisabled = opt.count === 0;
            const inputId = `filter--${facet.field}--${opt.size.replace(
              /[^a-zA-Z0-9]/g,
              ""
            )}--searchspring`;

            // Determine active state (if any of the underlying facet values is active)
            const isActive = facet.values.some(
              (fv) => fv.active && fv.value.split("|").includes(opt.size)
            );

            return `
              <li class="filter-item filter-item-checkbox ${
                isDisabled ? "filter-item--disabled" : ""
              }" data-type="checkbox">
                <label
                  data-filter
                  data-filter-item-label
                  class="filter-item__content filter-item__content--checkbox no-transition ${
                    isDisabled ? "filter-item__content--disabled" : ""
                  }"
                  for="${inputId}"
                >
                  <input
                    type="checkbox"
                    name="${facet.field}"
                    data-value-escaped="${encodeURIComponent(joinedValues)}"
                    value="${joinedValues}"
                    class="filter-item__input visually-hidden"
                    id="${inputId}"
                    data-field="${facet.field}"
                    data-value="${joinedValues}"
                    ${isActive ? "checked" : ""}
                    ${isDisabled ? "disabled" : ""}
                    data-filter="${facet.field}=${joinedValues}"
                    data-filter-item
                  >
                  <div class="filter-item__checkbox">
                    <svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <span class="filter-item__label type-body-regular">${
                    opt.size
                  }</span>
                  <span class="filter-item__count">${opt.count}</span>
                </label>
              </li>`;
          })
          .join("")}
      </ul>`;
  },

  renderDimensionFilter: (facet, dimensionType, minDefault, maxDefault) => {
    // Determine min & max dynamically from facet values when available
    let computedMin = Number.POSITIVE_INFINITY;
    let computedMax = Number.NEGATIVE_INFINITY;

    if (facet && Array.isArray(facet.values)) {
      facet.values.forEach((v) => {
        // Searchspring may provide either discrete or range facet objects
        if (v.low !== undefined && v.high !== undefined) {
          // Range style value
          const low = v.low === "*" ? 0 : parseFloat(v.low);
          const high = v.high === "*" ? parseFloat(v.low) : parseFloat(v.high);
          if (!isNaN(low)) computedMin = Math.min(computedMin, low);
          if (!isNaN(high)) computedMax = Math.max(computedMax, high);
        } else if (v.value !== undefined) {
          // Discrete value – can be pipe-separated list (e.g. "3|4")
          String(v.value)
            .split("|")
            .forEach((part) => {
              const num = parseFloat(part);
              if (!isNaN(num)) {
                computedMin = Math.min(computedMin, num);
                computedMax = Math.max(computedMax, num);
              }
            });
        }
      });
    }

    // Fallback to provided defaults when facet did not yield usable data
    if (!isFinite(computedMin)) computedMin = minDefault ?? 0;
    if (!isFinite(computedMax) || computedMax === 0) {
      computedMax = maxDefault ?? computedMin + 1;
    }

    // Label & formatting helpers
    const isPrice = dimensionType === "price";
    const unitLabel = isPrice ? "$" : "Ft.";
    const maxDisplay = isPrice ? `${computedMax}` : `${computedMax}`;

    // Build slider / input UI
    return `
      <div class="dimension-filter">
        <div class="dimension-slider" data-min="${computedMin}" data-max="${computedMax}">
          <div class="dimension-slider-fill" style="left: 0%; width: 100%;"></div>
          <div class="dimension-slider-handle" style="left: 0%;" data-position="min"></div>
          <div class="dimension-slider-handle" style="left: 100%;" data-position="max"></div>
        </div>
        <div class="dimension-inputs">
          <div class="dimension-input-group">
            <input 
              type="text" 
              class="dimension-input" 
              id="${dimensionType}-min" 
              value="${computedMin}" 
              data-field="${facet.field}" 
              data-dimension="${dimensionType}" 
              data-position="min"
            >
            <span class="dimension-label">${unitLabel}</span>
          </div>
          <span class="dimension-to">to</span>
          <div class="dimension-input-group">
            <input 
              type="text" 
              class="dimension-input" 
              id="${dimensionType}-max" 
              value="${maxDisplay}" 
              data-field="${facet.field}" 
              data-dimension="${dimensionType}" 
              data-position="max"
            >
            <span class="dimension-label">${unitLabel}</span>
          </div>
        </div>
      </div>
    `;
  },

  renderPriceFilter: (facet) => {
    return window.utils.renderDimensionFilter(facet, "price");
  },

  updateClassBasedOnWidth: () => {
    const productGrid = document.querySelector(".product-grid");
    if (!productGrid) {
      console.warn("Product grid not found!");
      return;
    }

    const swatches = document.querySelectorAll(
      ".product-card__swatches .new-swatch"
    );
    // const gridWidth = productGrid.offsetWidth;
    // const shouldBeLarge = gridWidth > 600;

    swatches.forEach((swatch) => {
      const isSidebarActive = swatch.classList.contains("active");

      if (isSidebarActive) {
        swatch.classList.remove("active");
      } else {
        swatch.classList.add("active");
      }
    });
  },
  buildFilterParams: (activeFilters) => {
    let str = "";
    activeFilters.forEach((values, field) => {
      values.forEach((value) => {
        if (
          field === "ss_price" ||
          field === "mfield_custom_length_rounded_ft_filter" ||
          field === "mfield_custom_width_rounded_ft_filter" ||
          field === "ss_all_width_filter"
        ) {
          const [min, max] = String(value).split("-");
          if (min && min !== "0") str += `&filter.${field}.low=${min}`;
          if (max && max !== "*") str += `&filter.${field}.high=${max}`;
        } else {
          str += `&filter.${field}=${encodeURIComponent(value)}`;
        }
      });
    });
    return str;
  },

  getFiltersFromUrl: () => {
    const params = new URLSearchParams(window.location.search);
    const active = new Map();
    const rangeBuffer = new Map();
    let sortParam = "";

    params.forEach((val, key) => {
      if (key.startsWith("filter.")) {
        const m = key.match(/^filter\.(.+?)(?:\.(low|high))?$/);
        if (!m) return;
        const [, field, part] = m;
        if (part) {
          if (!rangeBuffer.has(field)) rangeBuffer.set(field, {});
          rangeBuffer.get(field)[part] = val;
        } else {
          if (!active.has(field)) active.set(field, new Set());
          active.get(field).add(decodeURIComponent(val));
        }
      } else if (key.startsWith("sort.")) {
        sortParam = `${key}=${decodeURIComponent(val)}`;
      }
    });

    // consolidate range fields
    rangeBuffer.forEach((parts, field) => {
      const min = parts.low || "0";
      const max = parts.high || "*";
      const rangeVal = `${min}-${max}`;
      if (!active.has(field)) active.set(field, new Set());
      active.get(field).add(rangeVal);
    });

    // flatten Sets to Arrays for compatibility with rest of code
    const flattened = new Map();
    active.forEach((set, field) => {
      flattened.set(field, Array.from(set));
    });

    window.utils.filterParams = window.utils.buildFilterParams(flattened);
    window.utils.sortParam = sortParam;
    return flattened;
  },

  setDimensionFiltersFromActive: (activeFilters) => {
    const dimensionFields = [
      "ss_price",
      "mfield_custom_length_rounded_ft_filter",
      "mfield_custom_width_rounded_ft_filter",
      "ss_all_width_filter",
    ];

    activeFilters.forEach((values, field) => {
      if (!dimensionFields.includes(field)) return;
      values.forEach((value) => {
        const [min, max] = String(value).split("-");

        // dropdowns (if present)
        const minSelect = document.querySelector(
          `.dimension-dropdown[data-field="${field}"][data-position="min"]`
        );
        const maxSelect = document.querySelector(
          `.dimension-dropdown[data-field="${field}"][data-position="max"]`
        );
        if (minSelect) minSelect.value = min;
        if (maxSelect)
          maxSelect.value =
            max === "*"
              ? maxSelect.options[maxSelect.options.length - 1].value
              : max;

        // slider inputs (if present)
        const minInput = document.querySelector(
          `.dimension-input[data-field="${field}"][data-position="min"]`
        );
        const maxInput = document.querySelector(
          `.dimension-input[data-field="${field}"][data-position="max"]`
        );
        if (minInput) minInput.value = min;
        if (maxInput) maxInput.value = max === "*" ? "" : max;
      });
    });
  },

  setupFiltering: (options) => {
    const {
      sectionId,
      filtersContainer,
      activeFiltersContainer,
      clearAllBtn,
      filterToggleBtn,
      baseUrl,
      siteId,
      collectionHandle,
      productGrid,
      paginationContainer,
      productsPerPage,
      showLoading,
      hideLoading,
      loadProducts,
      totalCountElement,
      paginationType,
      setupInfiniteScroll,
      loadingOverlay,
      infiniteScrollingLoading,
    } = options;

    // 1️⃣  Build initial state from the URL (e.g. ?filter.color=Blue&sort.ss_boost=desc)
    let activeFilters = utils.getFiltersFromUrl();

    let currentFacets = [];

    // 2️⃣  Fall back to session-storage state ONLY if the URL did not specify anything
    if (activeFilters.size === 0) {
      const savedState =
        utils.loadSavedFilters && utils.loadSavedFilters(collectionHandle);
      if (savedState && savedState.filters) {
        Object.entries(savedState.filters).forEach(([field, values]) => {
          activeFilters.set(field, values);
        });
        if (savedState.sortParam) {
          window.utils.sortParam = savedState.sortParam;
        }
        window.utils.filterParams = utils.buildFilterParams(activeFilters);
      }
    }

    // Find the reset all button in filter toggle area
    const resetAllBtn = document.getElementById(`reset-all-${sectionId}`);
    // Hide the "Reset all" link by default; it will be shown when filters become active
    if (resetAllBtn) {
      resetAllBtn.style.display = "none";
    }

    // Toggle filter visibility
    if (filterToggleBtn) {
      filterToggleBtn.addEventListener("click", (e) => {
        e.preventDefault();

        utils.updateClassBasedOnWidth();
        filtersContainer.classList.toggle("active");
        filterToggleBtn.classList.toggle("active");
      });
    }

    // Clear all filters - traditional clear button
    const traditionalClearBtn = filtersContainer.querySelector(
      ".searchspring-clear-all"
    );
    if (traditionalClearBtn) {
      traditionalClearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        activeFilters.clear();

        // Uncheck all filter checkboxes
        const checkboxes =
          filtersContainer.querySelectorAll("[data-filter-item]");
        checkboxes.forEach((checkbox) => (checkbox.checked = false));

        // Update active filters display
        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );

        // Update clear button state
        traditionalClearBtn.classList.remove("active");

        // Reload products only
        utils.applyFilters(activeFilters, {
          baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        });

        // Reset all dimension sliders
        window.utils.resetAllDimensionFilters();
      });
    }

    // Legacy clear button support
    if (clearAllBtn) {
      clearAllBtn.addEventListener("click", () => {
        activeFilters.clear();

        // Uncheck all filter checkboxes
        const checkboxes =
          filtersContainer.querySelectorAll("[data-filter-item]");
        checkboxes.forEach((checkbox) => (checkbox.checked = false));

        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.applyFilters(activeFilters, {
          baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        });

        // Reset all dimension sliders
        window.utils.resetAllDimensionFilters();
      });
    }

    // Reset all button in the filter toggle area
    if (resetAllBtn) {
      resetAllBtn.addEventListener("click", (e) => {
        e.preventDefault();

        // Clear active filters map
        activeFilters.clear();

        // Uncheck all filter checkboxes inside filtersContainer
        const checkboxes =
          filtersContainer.querySelectorAll("[data-filter-item]");
        checkboxes.forEach((checkbox) => (checkbox.checked = false));

        // Deselect any selected swatches
        const colorSwatches = filtersContainer.querySelectorAll(
          ".filter-item__swatch.selected"
        );
        colorSwatches.forEach((swatch) => swatch.classList.remove("selected"));

        // Update UI displays
        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );

        // Apply cleared filters (reload products)
        utils.applyFilters(activeFilters, {
          baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        });

        // Reset dimension sliders
        window.utils.resetAllDimensionFilters();
      });
    }

    // Load initial facets
    utils.loadFacets(baseUrl, siteId, collectionHandle).then((facets) => {
      currentFacets = facets;
      utils.renderFilters(facets, filtersContainer);
      utils.setupFilterEventListeners(
        filtersContainer,
        activeFilters,
        activeFiltersContainer,
        {
          baseUrl,
          siteId,
          collectionHandle,
          sectionId,
          productGrid,
          paginationContainer,
          productsPerPage,
          showLoading,
          hideLoading,
          loadProducts,
          totalCountElement,
          filtersContainer,
          paginationType,
          setupInfiniteScroll,
          loadingOverlay,
          infiniteScrollingLoading,
        }
      );

      // === NEW: bring UI & product grid in sync with any restored filters ===
      utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);

      // Also sync sliders / dimension dropdowns
      utils.setDimensionFiltersFromActive(activeFilters);

      if (activeFilters.size > 0) {
        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.applyFilters(activeFilters, options);
      }
    });
  },

  loadFacets: async (baseUrl, siteId, collectionHandle) => {
    try {
      // Facets are already included in the main search response
      // Add required parameters: page and resultsPerPage
      const separator = baseUrl.includes('?') ? '&' : '?';
      const facetsUrl = `${baseUrl}${separator}page=1&resultsPerPage=1`;
      const response = await fetch(facetsUrl);
      const data = await response.json();
      return data.facets || [];
    } catch (error) {
      console.error("Error loading facets:", error);
      return [];
    }
  },

  setupFilterEventListeners: (
    filtersContainer,
    activeFilters,
    activeFiltersContainer,
    options
  ) => {
    // Filter group toggle using traditional Shopify structure
    filtersContainer.addEventListener("click", (e) => {
      if (e.target.closest(".collection-sidebar__heading")) {
        const filterGroup = e.target.closest(".collection-sidebar__group");
        const content = filterGroup.querySelector(
          ".collection-sidebar__content"
        );
        const heading = filterGroup.querySelector(
          ".collection-sidebar__heading"
        );
        const chevronIcon = heading.querySelector(".icon-chevron-down");

        const isExpanded = !heading.classList.contains("closed");

        // Ensure the content element is prepared for animation
        content.style.overflow = "hidden";
        if (!content.style.transition) {
          content.style.transition = "max-height 0.35s ease"; // slightly shorter & smoother
        }

        if (isExpanded) {
          // Collapse with smoother animation
          const startHeight = content.scrollHeight + "px";
          content.style.maxHeight = startHeight;
          // Use RAF to ensure start height is applied before animating to 0
          requestAnimationFrame(() => {
            content.style.maxHeight = "0px";
          });

          heading.classList.add("closed");
          heading.classList.remove("open");

          const onCollapseEnd = () => {
            // Ensure collapsed state retains without triggering additional reflow
            content.style.maxHeight = "0px";

            content.removeEventListener("transitionend", onCollapseEnd);
          };
          content.addEventListener("transitionend", onCollapseEnd, {
            once: true,
          });

          if (chevronIcon) {
            chevronIcon.style.transform = "rotate(0deg)";
          }
        } else {
          // Expand with smoother animation
          heading.classList.remove("closed");
          heading.classList.add("open");

          // Prepare the element for measuring
          content.style.display = "block";
          content.style.maxHeight = "0px";

          // Wait for next frame then animate to full height
          requestAnimationFrame(() => {
            const targetHeight = content.scrollHeight + "px";
            content.style.maxHeight = targetHeight;
          });

          const onExpandEnd = () => {
            // Clean-up to allow natural sizing
            content.style.maxHeight = "";
            content.style.overflow = "";
            content.removeEventListener("transitionend", onExpandEnd);
          };
          content.addEventListener("transitionend", onExpandEnd, {
            once: true,
          });

          if (chevronIcon) {
            chevronIcon.style.transform = "rotate(180deg)";
          }
        }
      }
    });

    // Checkbox filters - only reload products, not filters
    filtersContainer.addEventListener("change", (e) => {
      if (
        e.target.type === "checkbox" &&
        e.target.hasAttribute("data-filter-item")
      ) {
        const field = e.target.dataset.field;
        const value = e.target.dataset.value;

        if (e.target.checked) {
          if (!activeFilters.has(field)) {
            activeFilters.set(field, []);
          }

          // Handle comma-separated values (for size filter)
          if (value.includes(",")) {
            const values = value.split(",");
            activeFilters.set(field, [
              ...new Set([...activeFilters.get(field), ...values]),
            ]);
          } else {
            activeFilters.get(field).push(value);
          }
        } else {
          if (activeFilters.has(field)) {
            const values = activeFilters.get(field);

            if (value.includes(",")) {
              // Handle comma-separated values – remove all underlying selections
              const valuesToRemove = value.split(",");
              activeFilters.set(
                field,
                values.filter((v) => !valuesToRemove.includes(v))
              );
            } else {
              const index = values.indexOf(value);
              if (index > -1) {
                values.splice(index, 1);
              }
            }

            if (activeFilters.get(field).length === 0) {
              activeFilters.delete(field);
            }
          }
        }

        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.applyFilters(activeFilters, options);
      }
    });

    // Color filters
    filtersContainer.addEventListener("click", (e) => {
      if (e.target.classList.contains("filter-item__swatch")) {
        const field = e.target.dataset.field;
        const value = e.target.dataset.value;
        const color = e.target.dataset.color;

        e.target.classList.toggle("selected");

        if (e.target.classList.contains("selected")) {
          if (!activeFilters.has(field)) {
            activeFilters.set(field, []);
          }
          activeFilters.get(field).push(value);
        } else {
          if (activeFilters.has(field)) {
            const values = activeFilters.get(field);
            const index = values.indexOf(value);
            if (index > -1) {
              values.splice(index, 1);
            }
            if (values.length === 0) {
              activeFilters.delete(field);
            }
          }
        }

        utils.updateActiveFiltersDisplay(
          activeFiltersContainer,
          activeFilters,
          options
        );
        utils.applyFilters(activeFilters, options);
      }
    });

    // normal filter
    filtersContainer.addEventListener("change", (e) => {
      if (e.target.classList.contains("filter-item__input")) {
        const field = e.target.dataset.field;
        const value = e.target.dataset.value;
        // console.log(field, value, "field, value, hello");

        e?.target?.click();
      }
    });

    // === New: Slider handle dragging ===
    const startDrag = (handle, dimensionFilter, e) => {
      e.preventDefault();
      const slider = dimensionFilter.querySelector(".dimension-slider");
      const sliderFill = dimensionFilter.querySelector(
        ".dimension-slider-fill"
      );
      const minHandle = dimensionFilter.querySelector(
        '.dimension-slider-handle[data-position="min"]'
      );
      const maxHandle = dimensionFilter.querySelector(
        '.dimension-slider-handle[data-position="max"]'
      );
      const dimension = handle.dataset.position; // min or max
      const field =
        dimensionFilter.querySelector(".dimension-input").dataset.field; // any input
      const rangeName =
        dimensionFilter.querySelector(".dimension-input").dataset.dimension;
      const minInput = document.getElementById(`${rangeName}-min`);
      const maxInput = document.getElementById(`${rangeName}-max`);

      const sliderMin = parseFloat(slider.dataset.min) || 0;
      const sliderMax = parseFloat(slider.dataset.max) || 1;
      const rangeSpan = sliderMax - sliderMin;

      const moveListener = (moveEvt) => {
        const clientX = moveEvt.touches
          ? moveEvt.touches[0].clientX
          : moveEvt.clientX;
        const rect = slider.getBoundingClientRect();
        let percent = ((clientX - rect.left) / rect.width) * 100;
        percent = Math.min(Math.max(percent, 0), 100);

        // prevent crossing handles
        const otherPercent = parseFloat(
          (dimension === "min" ? maxHandle : minHandle).style.left
        );
        if (dimension === "min" && percent > otherPercent)
          percent = otherPercent;
        if (dimension === "max" && percent < otherPercent)
          percent = otherPercent;

        handle.style.left = `${percent}%`;

        if (dimension === "min") {
          sliderFill.style.left = `${percent}%`;
          sliderFill.style.width = `${otherPercent - percent}%`;
        } else {
          sliderFill.style.width = `${percent - otherPercent}%`;
        }

        // Calculate value & update input
        const value = Math.round(sliderMin + (percent / 100) * rangeSpan);
        if (dimension === "min") {
          minInput.value = value;
        } else {
          maxInput.value = value;
        }
      };

      const endListener = () => {
        document.removeEventListener("mousemove", moveListener);
        document.removeEventListener("touchmove", moveListener);
        document.removeEventListener("mouseup", endListener);
        document.removeEventListener("touchend", endListener);
        minInput.dispatchEvent(new Event("blur", { bubbles: true })); // trigger apply logic
      };

      document.addEventListener("mousemove", moveListener);
      document.addEventListener("touchmove", moveListener, { passive: false });
      document.addEventListener("mouseup", endListener);
      document.addEventListener("touchend", endListener);
    };

    filtersContainer.addEventListener("mousedown", (e) => {
      if (e.target.classList.contains("dimension-slider-handle")) {
        startDrag(e.target, e.target.closest(".dimension-filter"), e);
      }
    });
    filtersContainer.addEventListener(
      "touchstart",
      (e) => {
        if (e.target.classList.contains("dimension-slider-handle")) {
          startDrag(e.target, e.target.closest(".dimension-filter"), e);
        }
      },
      { passive: false }
    );
    // === End drag support ===

    // Dimension inputs
    filtersContainer.addEventListener("input", (e) => {
      if (e.target.classList.contains("dimension-input")) {
        const field = e.target.dataset.field;
        const dimension = e.target.dataset.dimension;
        const position = e.target.dataset.position;

        // Update slider position
        const minInput = document.getElementById(`${dimension}-min`);
        const maxInput = document.getElementById(`${dimension}-max`);
        const dimensionFilter = e.target.closest(".dimension-filter");
        const slider = dimensionFilter.querySelector(".dimension-slider");
        const sliderFill = dimensionFilter.querySelector(
          ".dimension-slider-fill"
        );
        const minHandle = dimensionFilter.querySelector(
          '.dimension-slider-handle[data-position="min"]'
        );
        const maxHandle = dimensionFilter.querySelector(
          '.dimension-slider-handle[data-position="max"]'
        );

        // Dynamic range based on slider dataset
        const sliderMin = parseFloat(slider.dataset.min) || 0;
        const sliderMax = parseFloat(slider.dataset.max) || 1; // avoid divide by zero
        const rangeSpan = sliderMax - sliderMin;

        const minValueRaw = minInput.value.replace(/[^0-9.]/g, ""); // strip non-numeric
        const maxValueRaw = maxInput.value.replace(/[^0-9.]/g, "");
        const minValue = parseFloat(minValueRaw) || sliderMin;
        const maxValue = parseFloat(maxValueRaw) || sliderMax;

        if (position === "min") {
          const minPercent = Math.min(
            Math.max(((minValue - sliderMin) / rangeSpan) * 100, 0),
            100
          );
          minHandle.style.left = `${minPercent}%`;
          sliderFill.style.left = `${minPercent}%`;
          sliderFill.style.width = `${Math.max(0, 100 - minPercent)}%`;
        } else {
          const maxPercent = Math.min(
            Math.max(((maxValue - sliderMin) / rangeSpan) * 100, 0),
            100
          );
          maxHandle.style.left = `${maxPercent}%`;
          sliderFill.style.width = `${Math.max(
            0,
            maxPercent - parseFloat(sliderFill.style.left)
          )}%`;
        }
      }
    });

    // Apply dimension filters on blur
    filtersContainer.addEventListener(
      "blur",
      (e) => {
        if (e.target.classList.contains("dimension-input")) {
          const field = e.target.dataset.field;
          const dimension = e.target.dataset.dimension;

          const minInput = document.getElementById(`${dimension}-min`);
          const maxInput = document.getElementById(`${dimension}-max`);

          const dimensionFilter = e.target.closest(".dimension-filter");
          const slider = dimensionFilter.querySelector(".dimension-slider");
          const sliderMin = parseFloat(slider.dataset.min) || 0;
          const sliderMax = parseFloat(slider.dataset.max) || 0;

          let minValue = parseFloat(minInput.value.replace(/[^0-9.]/g, ""));
          let maxRaw = maxInput.value;
          let maxNumericPart = parseFloat(maxRaw.replace(/[^0-9.]/g, ""));

          // Clamp values within slider range
          if (isNaN(minValue)) minValue = sliderMin;
          minValue = Math.max(sliderMin, Math.min(minValue, sliderMax));

          let maxValue;
          if (/\+$/.test(maxRaw.trim())) {
            maxValue = sliderMax; // always numeric for UI
          } else {
            maxValue = isNaN(maxNumericPart) ? sliderMax : maxNumericPart;
            maxValue = Math.min(Math.max(maxValue, minValue), sliderMax);
          }

          // Update inputs with clamped values
          minInput.value = minValue;
          maxInput.value = maxValue;

          // Update slider handles positions to reflect clamped values
          const minPercent =
            ((minValue - sliderMin) / (sliderMax - sliderMin)) * 100;
          const maxPercent =
            maxValue === "*"
              ? 100
              : ((maxValue - sliderMin) / (sliderMax - sliderMin)) * 100;

          const minHandle = dimensionFilter.querySelector(
            '.dimension-slider-handle[data-position="min"]'
          );
          const maxHandle = dimensionFilter.querySelector(
            '.dimension-slider-handle[data-position="max"]'
          );
          const sliderFill = dimensionFilter.querySelector(
            ".dimension-slider-fill"
          );
          if (minHandle && maxHandle && sliderFill) {
            minHandle.style.left = `${minPercent}%`;
            maxHandle.style.left = `${maxPercent}%`;
            sliderFill.style.left = `${minPercent}%`;
            sliderFill.style.width = `${maxPercent - minPercent}%`;
          }

          // Guard: only push filter when changed from defaults
          const defaultRange = `${sliderMin}-${sliderMax}`;
          const userRange = `${minValue}-${maxValue}`;

          if (userRange !== defaultRange) {
            if (!activeFilters.has(field)) {
              activeFilters.set(field, []);
            }

            // Remove any existing range values for this field
            activeFilters.set(
              field,
              activeFilters.get(field).filter((v) => !v.includes("-"))
            );

            activeFilters.get(field).push(userRange);

            utils.updateActiveFiltersDisplay(
              activeFiltersContainer,
              activeFilters,
              options
            );
            utils.applyFilters(activeFilters, options);
          }
        }
      },
      true
    );

    // Show more / Show less toggle for long lists
    filtersContainer.addEventListener("click", (e) => {
      const btn = e.target.closest(".show-more-btn");
      if (!btn) return;

      const list = btn.closest(".filter-drawer__list");
      if (!list) return;

      const hiddenItems = list.querySelectorAll(".filter-item--hidden");
      const isCollapsed = btn.dataset.state === "collapsed";

      hiddenItems.forEach((li) => {
        li.style.display = isCollapsed ? "block" : "none";
      });

      btn.textContent = isCollapsed ? "View less" : "View more";
      btn.dataset.state = isCollapsed ? "expanded" : "collapsed";
    });

    // inside setupFilterEventListeners, after the checkbox/change listeners
    filtersContainer.addEventListener("change", (e) => {
      if (!e.target.classList.contains("dimension-dropdown")) return;

      const field = e.target.dataset.field;
      const container = e.target.closest(".dimension-filter-dropdown");
      const minSel = container.querySelector('[data-position="min"]');
      const maxSel = container.querySelector('[data-position="max"]');

      const minVal = minSel.value || 0; // API treats 0 as open low
      const maxVal = maxSel.value || "*"; // "*" signals open high

      // update activeFilters map kept in setupFiltering
      activeFilters.set(
        field,
        minSel.value || maxSel.value ? [`${minVal}-${maxVal}`] : []
      );

      utils.updateActiveFiltersDisplay(
        activeFiltersContainer,
        activeFilters,
        options
      );
      utils.applyFilters(activeFilters, options);
    });
  },

  updateActiveFiltersDisplay: (
    activeFiltersContainer,
    activeFilters,
    options = null,
    filtersMainContainer = null
  ) => {
    // Persist the most recent options object so that nested or subsequent calls
    // (which may omit the argument) can still access the data required by
    // utils.applyFilters.
    if (options) {
      window.utils.lastApplyOptions = options;
    }

    // Fallback – use the last saved options when none are provided.
    const persistedOptions = window.utils.lastApplyOptions || {};

    // Find both active filter containers - the traditional one and the bar
    const filtersContainer = activeFiltersContainer;
    const filtersBarContainer = document.querySelector(".active-filters-bar");

    // Find traditional active filters container
    const traditionalActiveFilters = document.querySelector(
      ".active-filters-display"
    );
    const traditionalActiveFiltersList = document.querySelector(
      ".active-filters-list"
    );

    if (!filtersContainer && !filtersBarContainer && !traditionalActiveFilters)
      return;

    // Update clear button state
    const clearBtn = document.querySelector(".searchspring-clear-all");
    if (clearBtn) {
      if (activeFilters.size > 0) {
        clearBtn.classList.add("active");
      } else {
        clearBtn.classList.remove("active");
      }
    }

    // Create HTML for filter tags
    let activeFiltersHTML = "";
    let resetButtonHTML = "";

    // If there are active filters, show the reset button
    if (activeFilters.size > 0) {
      resetButtonHTML = `
				<button class="active-filters-reset" data-action="reset-all">
					Reset all
				</button>
			`;
    }

    // Map facet fields to display names
    const facetLabels = {
      ss_price: "Price",
      vendor: "Brand",
      mfield_custom_primary_style: "Style",
      mfield_new_my_fields_sub_category: "Product Type",
      variant_mfield_new_my_fields_filter_color: "Color",
      variant_mfield_custom_rug_size_bucket_filter: "Size",
      variant_mfield_custom_width_filter: "Width",
      variant_mfield_custom_variant_width_rounded_ft_filter: "Width (ft.)",
      variant_mfield_custom_variant_length_rounded_ft_filter: "Length",
      mfield_custom_rug_pattern: "Pattern",
    };

    activeFilters.forEach((values, field) => {
      // Special handling for Quickship (lead time)
      const QUICKSHIP_SET = new Set(
        [
          "1-2 weeks",
          "1-3 weeks",
          "1-3 Weeks",
          "2-4 weeks",
          "3-4 weeks",
          "4 weeks",
        ].map((s) => s.toLowerCase())
      );

      const shown = new Set();
      const fieldLabel = facetLabels[field] || field;

      // Aggregate Quickship tag once per field
      // if (field === "variant_mfield_new_my_fields_lead_time") {
      //   const quickshipActive = values.filter((v) =>
      //     QUICKSHIP_SET.has(String(v).toLowerCase())
      //   );
      //   if (quickshipActive.length) {
      //     const aggregated = [
      // 	'1-2 weeks',
      // 	'1-3 weeks',
      // 	'1-3 Weeks',
      // 	'2-4 weeks',
      // 	'3-4 weeks',
      // 	'4 weeks',
      // ].join(',');
      //     const dedupeKeyAgg = `${field}__quickship`;
      //     if (!shown.has(dedupeKeyAgg)) {
      //       shown.add(dedupeKeyAgg);
      //       activeFiltersHTML += `
      //         <span class="active-filter-tag" data-field="${field}" data-value="${aggregated}">
      //           Quickship
      //           <button class="remove-filter" data-field="${field}" data-value="${aggregated}">×</button>
      //         </span>
      //       `;
      //     }
      //   }
      // }
      const widthBucketValues = [
        "under 20",
        "20 - 24",
        "25-29",
        "30-34",
        "35-39",
        "40-44",
        "45-49",
        "50-54",
        "55-59",
        "60-69",
        "70-79",
        "80-89",
        "90-99",
        "100-109",
        "110-119",
        "120-129",
        "130+",
      ];

      values.forEach((value) => {
        // Skip individual quickship values (already aggregated)
        // if (
        //   field === "variant_mfield_new_my_fields_lead_time" &&
        //   QUICKSHIP_SET.has(String(value).toLowerCase())
        // ) {
        //   return;
        // }

        let displayValue = value;

        // Format display values
        if (field === "ss_price") {
          // Price display
          let [min, max] = value.split("-");

          // Detect open-ended (max is * or contains +)
          const isOpenEnded = max === "*" || /\+$/.test(max);

          if (isOpenEnded) {
            // Show as "$min+" (no upper bound)
            displayValue = `$${min}+`;
          } else {
            // Otherwise standard min-max
            displayValue = `$${min} - $${max}`;
          }
        } else if (field === "variant_mfield_new_my_fields_filter_color") {
          // Color display - extract color name from JSON format
          try {
            // Handle formats like: ["Color"] or ["Color1"]|["Color2"]
            let colorName = value;

            // If it contains JSON-like brackets, try to parse
            if (value.includes("[") && value.includes("]")) {
              const parsed = JSON.parse(value.split("|")[0]);
              colorName = Array.isArray(parsed) ? parsed[0] : parsed;
            } else {
              // Simple cleanup for non-JSON formats
              colorName = value.replace(/\[|\]|"/g, "").split("|")[0];
            }

            displayValue = colorName;
          } catch (e) {
            // Fallback: just clean up the value
            displayValue = value.replace(/\[|\]|"/g, "").split("|")[0];
          }
        } else if (field === "variant_mfield_custom_width_filter") {
          // Map numeric widths to bucket label
          const bucketForNum = (n) => {
            if (isNaN(n)) return null;
            if (n < 20) return 'Under 20"';
            if (n < 25) return '20 - 24"';
            if (n < 30) return '25-29"';
            if (n < 35) return '30-34"';
            if (n < 40) return '35-39"';
            if (n < 45) return '40-44"';
            if (n < 50) return '45-49"';
            if (n < 55) return '50-54"';
            if (n < 60) return '55-59"';
            if (n < 70) return '60-69"';
            if (n < 80) return '70-79"';
            if (n < 90) return '80-89"';
            if (n < 100) return '90-99"';
            if (n < 110) return '100-109"';
            if (n < 120) return '110-119"';
            if (n < 130) return '120-129"';
            return '130+"';
          };

          const numeric = parseFloat(value);
          const label = bucketForNum(numeric);
          displayValue = label || `${value}"`;

          // Build aggregated list of all widths in this bucket
          const aggregated = values
            .filter((v) => bucketForNum(parseFloat(v)) === label)
            .join(",");

          value = aggregated; // use aggregated list as data-value for chip & removal
        } else if (
          field === "mfield_custom_length_rounded_ft_filter" ||
          field === "mfield_custom_width_rounded_ft_filter"
        ) {
          // Dimension display
          const [min, max] = value.split("-");
          displayValue = `${min} - ${max} ft`;
        } else if (value.includes("|")) {
          // Other pipe-separated values
          displayValue = value.split("|")[0];
        }

        // Clean up any category paths
        if (displayValue.includes(">")) {
          displayValue = displayValue.split(">").pop().trim();
        }

        // Avoid showing duplicates for Standard Sizes (size bucket filter)
        const dedupeKey = `${field}__${displayValue}`;
        if (!shown.has(dedupeKey)) {
          shown.add(dedupeKey);

          activeFiltersHTML += `
					<span class="active-filter-tag" data-field="${field}" data-value="${value}">
						${displayValue}
						<button class="remove-filter" data-field="${field}" data-value="${value}">×</button>
					</span>
				`;
        }
      });
    });

    const isMobile = window.innerWidth <= 768;

    // Update all containers
    if (filtersContainer) {
      filtersContainer.innerHTML = activeFiltersHTML;
    }

    if (filtersBarContainer && !isMobile) {
      filtersBarContainer.innerHTML = activeFiltersHTML;
      filtersBarContainer.style.display = activeFiltersHTML ? "flex" : "none";
    }

    // Update traditional active filters display only on desktop
    if (!isMobile && traditionalActiveFiltersList) {
      traditionalActiveFiltersList.innerHTML =
        resetButtonHTML + activeFiltersHTML;
    }

    if (!isMobile && traditionalActiveFilters) {
      traditionalActiveFilters.style.display = activeFiltersHTML
        ? "flex"
        : "none";
    } else if (isMobile && traditionalActiveFilters) {
      traditionalActiveFilters.style.display = "none";
    }

    // Add event listeners for removing filters and reset button
    const addRemoveListeners = (container) => {
      if (!container) return;

      // Handle individual filter removal
      const removeButtons = container.querySelectorAll(".remove-filter");
      removeButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const field = button.dataset.field;
          const value = button.dataset.value;

          if (activeFilters.has(field)) {
            const values = activeFilters.get(field);

            if (value.includes(",")) {
              // Handle comma-separated values – remove all underlying selections
              const valuesToRemove = value.split(",");
              activeFilters.set(
                field,
                values.filter((v) => !valuesToRemove.includes(v))
              );
            } else {
              const index = values.indexOf(value);
              if (index > -1) {
                values.splice(index, 1);
              }
            }

            if (activeFilters.get(field).length === 0) {
              activeFilters.delete(field);
            }

            // Update UI elements
            if (field === "variant_mfield_new_my_fields_filter_color") {
              // Update color swatch selection
              const colorSwatches = document.querySelectorAll(
                ".filter-item__swatch"
              );
              colorSwatches.forEach((swatch) => {
                if (swatch.dataset.value === value) {
                  swatch.classList.remove("selected");
                }
              });
            } else {
              // Update checkbox state
              const checkbox = document.querySelector(
                `input[data-field="${field}"][data-value="${value}"]`
              );
              if (checkbox) {
                checkbox.checked = false;
              }
            }

            utils.updateActiveFiltersDisplay(
              activeFiltersContainer,
              activeFilters,
              persistedOptions
            );
            utils.applyFilters(activeFilters, persistedOptions);
          }
        });
      });

      // Handle reset all button
      const resetButton = container.querySelector(".active-filters-reset");
      if (resetButton) {
        resetButton.addEventListener("click", () => {
          // Clear all active filters
          activeFilters.clear();

          // Uncheck all checkboxes
          const checkboxes = document.querySelectorAll("[data-filter-item]");
          checkboxes.forEach((checkbox) => (checkbox.checked = false));

          // Deselect all color swatches
          const colorSwatches = document.querySelectorAll(
            ".filter-item__swatch"
          );
          colorSwatches.forEach((swatch) =>
            swatch.classList.remove("selected")
          );

          // Update display and apply filters
          utils.updateActiveFiltersDisplay(
            activeFiltersContainer,
            activeFilters,
            persistedOptions
          );
          utils.applyFilters(activeFilters, persistedOptions);
        });
      }
    };

    // Add listeners to all containers
    addRemoveListeners(filtersContainer);
    addRemoveListeners(filtersBarContainer);
    addRemoveListeners(traditionalActiveFiltersList);

    // Update Filter button count specific to section
    const countFilters = Array.from(activeFilters.values()).reduce(
      (sum, arr) => sum + arr.length,
      0
    );
    if (filtersContainer && filtersContainer.id) {
      const idSegments = filtersContainer.id.split("-");
      const sectionId = idSegments[idSegments.length - 1];
      if (sectionId) {
        const toggleBtn = document.getElementById(`filter-toggle-${sectionId}`);
        if (toggleBtn) {
          const labelSpan = toggleBtn.querySelector("span");
          if (labelSpan) {
            labelSpan.textContent =
              countFilters > 0 ? `Filter (${countFilters})` : "Filter";
          }
        }
      }
    }

    // Toggle visibility of mobile active filters wrapper if present
    if (filtersContainer) {
      const mobileWrapper = filtersContainer.closest(".mobile-active-filters");
      if (mobileWrapper) {
        mobileWrapper.style.display = activeFiltersHTML ? "block" : "none";
      }
    }

    // Toggle visibility of the "Reset all" link next to the filter toggle
    if (persistedOptions && persistedOptions.sectionId) {
      const resetBtn = document.getElementById(
        `reset-all-${persistedOptions.sectionId}`
      );
      if (resetBtn) {
        resetBtn.style.display = activeFilters.size > 0 ? "inline" : "none";
      }
    } else {
      // Fallback – toggle all reset-all links if sectionId is unavailable
      document
        .querySelectorAll(".reset-all:not(.show-more-btn)")
        .forEach((btn) => {
          btn.style.display = activeFilters.size > 0 ? "inline" : "none";
        });
    }

    // Additionally toggle any mobile reset links for this section (class .mobile-reset-all)
    document
      .querySelectorAll(
        `.mobile-reset-all#reset-all-${persistedOptions.sectionId}`
      )
      .forEach((btn) => {
        btn.style.display = activeFilters.size > 0 ? "inline" : "none";
      });

    const allActiveFilters = document
      .querySelector(".mobile-active-filters-list")
      .querySelectorAll(".active-filter-tag");
    const allActiveFiltersCount = allActiveFilters.length;
    // -------- mobile count badge ----------
    const mobileCountEls = document.querySelectorAll(
      ".active-filters-count-mobile"
    );
    if (allActiveFiltersCount > 0) {
      mobileCountEls.forEach((el) => {
        el.textContent = `(${allActiveFiltersCount})`;
      });
    } else {
      mobileCountEls.forEach((el) => {
        el.textContent = ``;
      });
    }
  },

  applyFilters: (activeFilters, options) => {
    const {
      baseUrl,
      productGrid,
      paginationContainer,
      productsPerPage,
      showLoading,
      hideLoading,
      loadProducts,
      totalCountElement,
      // NEW: needed to refresh facets after applying filters
      filtersContainer,
      siteId,
      collectionHandle,
      sectionId,
    } = options;

    let filterParams = ``;

    // Add active filters to URL
    activeFilters.forEach((values, field) => {
      values.forEach((value) => {
        if (
          field === "ss_price" ||
          field === "mfield_custom_length_rounded_ft_filter" ||
          field === "mfield_custom_width_rounded_ft_filter" ||
          field === "ss_all_width_filter"
        ) {
          // Handle numeric range fields
          const [min, max] = value.split("-");
          if (min && min !== "0") filterParams += `&filter.${field}.low=${min}`;
          if (max) {
            filterParams += `&filter.${field}.high=${max}`;
          }
          // If max is * (open ended) we omit high param so API treats it as open upper bound
        } else if (field === "variant_mfield_new_my_fields_filter_color") {
          filterParams += `&filter.${field}=${encodeURIComponent(value)}`;
        } else {
          filterParams += `&filter.${field}=${encodeURIComponent(value)}`;
        }
      });
    });

    const globalUrl = window.utils.globalUrl(
      baseUrl,
      (page = 1),
      productsPerPage
    );
    window.utils.filterParams = filterParams;
    const url = `${globalUrl}${filterParams}${
      window.utils.sortParam ? `&${window.utils.sortParam}` : ""
    }`;

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "instant" });
    scrollToTop();

    // Persist current filter and sort state
    utils.saveActiveFilters(activeFilters, options.collectionHandle || "");

    // Apply filters and update facets instantly from the same response
    loadProducts({
      page: 1,
      baseUrl: url,
      productsPerPage,
      productGrid,
      paginationContainer,
      showLoading,
      hideLoading,
      callback: (data) => {
        // If API returned facets, re-render filters with latest counts immediately
        if (data && Array.isArray(data.facets)) {
          utils.renderFilters(data.facets, filtersContainer);
          utils.syncFilterUIWithActiveFilters(filtersContainer, activeFilters);
          utils.setDimensionFiltersFromActive(activeFilters);
        } else {
          // Fallback: fetch facets using the same URL
          utils
            .loadFacets(url, siteId, collectionHandle)
            .then((facets) => {
              utils.renderFilters(facets, filtersContainer);
              utils.syncFilterUIWithActiveFilters(
                filtersContainer,
                activeFilters
              );
              utils.setDimensionFiltersFromActive(activeFilters);
            })
            .catch((err) => console.error("Error refreshing facets:", err));
        }
      },
      totalCountElement,
      filterParams,
      source: "filter-apply",
      sectionId,
    });

    (() => {
      const newUrl = new URL(window.location.pathname, window.location.origin);
      let search = "?page=1"; // always reset to page 1
      if (filterParams) search += `${filterParams}`; // e.g. &filter.color=Blue
      if (window.utils.sortParam) search += `&${window.utils.sortParam}`;
      newUrl.search = search;
      window.history.pushState({}, "", newUrl);
    })();
  },

  // === helper to reset a single dimension filter (price/width/length) ===
  resetDimensionFilter: (field) => {
    const inputs = document.querySelectorAll(
      `.dimension-input[data-field="${field}"]`
    );
    if (!inputs || inputs.length === 0) return;

    const dimensionFilter = inputs[0].closest(".dimension-filter");
    if (!dimensionFilter) return;

    const slider = dimensionFilter.querySelector(".dimension-slider");
    const sliderFill = dimensionFilter.querySelector(".dimension-slider-fill");
    const minHandle = dimensionFilter.querySelector(
      '.dimension-slider-handle[data-position="min"]'
    );
    const maxHandle = dimensionFilter.querySelector(
      '.dimension-slider-handle[data-position="max"]'
    );

    const sliderMin = parseFloat(slider.dataset.min) || 0;
    const sliderMax = parseFloat(slider.dataset.max) || 0;

    // Update input fields
    inputs.forEach((input) => {
      const dim = input.dataset.position; // min or max
      const isPrice = input.dataset.dimension === "price";
      if (dim === "min") {
        input.value = sliderMin;
      } else {
        input.value = sliderMax;
      }
    });

    // Reset slider visuals
    if (minHandle) minHandle.style.left = "0%";
    if (maxHandle) maxHandle.style.left = "100%";
    if (sliderFill) {
      sliderFill.style.left = "0%";
      sliderFill.style.width = "100%";
    }
  },

  // Reset all dimension filters on page
  resetAllDimensionFilters: () => {
    document.querySelectorAll(".dimension-input").forEach((input) => {
      window.utils.resetDimensionFilter(input.dataset.field);
    });
  },

  renderWidthBucketFilter: (facet) => {
    // We are no longer bucketing widths. We'll build a map of unique numeric widths and aggregate their counts.

    const buckets = []; // kept for backward compatibility but unused further down

    // Helper to extract numeric inches from a raw facet value token
    const toNumber = (token) => {
      const num = parseFloat(String(token).replace(/[^0-9.]/g, ""));
      return isNaN(num) ? null : num;
    };

    // Build a map of unique numeric widths -> aggregated { count, active }
    const widthMap = new Map();

    (facet.values || []).forEach((facetValue) => {
      if (!facetValue) return;

      const raw = String(facetValue.value || "");

      // Ignore multi-value tokens that contain the pipe separator
      if (raw.includes("|")) return;

      // Ignore tokens that include an inch symbol, e.g., '20"'
      if (raw.includes('"')) return;

      const n = toNumber(raw);
      if (n === null) return; // skip unparsable values

      const key = n; // numeric key; JS Map treats 20 and 20.0 the same

      const entry = widthMap.get(key) || {
        count: 0,
        active: false,
        // Prefer the first encountered raw string as a label; fallback later
        label: undefined,
      };

      entry.count += facetValue.count;
      if (facetValue.active) entry.active = true;

      // Capture a display label once (e.g., "36" vs 36). If none stored yet, use current raw.
      if (!entry.label) {
        entry.label = raw;
      }

      widthMap.set(key, entry);
    });

    // Convert widthMap into a sorted array of [num, data]
    const entries = Array.from(widthMap.entries()).sort((a, b) => a[0] - b[0]);

    // group entries in under 20 , 20 - 24, 25-29, 30-34, 35-39, 40-44, 45-49, 50-54, 55-59, 60-69 , 70-79 , 80-89, 90-99, 100-109, 110-119, 120-129, 130+

    const groupedEntries = entries.reduce((acc, [num, data]) => {
      const key =
        num < 20
          ? "under 20"
          : num < 25
          ? "20 - 24"
          : num < 30
          ? "25-29"
          : num < 35
          ? "30-34"
          : num < 40
          ? "35-39"
          : num < 45
          ? "40-44"
          : num < 50
          ? "45-49"
          : num < 55
          ? "50-54"
          : num < 60
          ? "55-59"
          : num < 70
          ? "60-69"
          : num < 80
          ? "70-79"
          : num < 90
          ? "80-89"
          : num < 100
          ? "90-99"
          : num < 110
          ? "100-109"
          : num < 120
          ? "110-119"
          : num < 130
          ? "120-129"
          : "130+";
      if (!acc[key]) acc[key] = [];
      acc[key].push([num, data]);
      return acc;
    }, {});

    const groupDisplay = Object.entries(groupedEntries).map(([key, value]) => {
      return {
        label: key,
        count: value.reduce((sum, [num, data]) => sum + data.count, 0),
        active: value.some(([num, data]) => data.active),
        values: value.map(([num, data]) => data.label || `${num}`), // collect underlying raw labels
      };
    });

    // Desired display order
    const displayOrder = [
      "under 20",
      "20 - 24",
      "25-29",
      "30-34",
      "35-39",
      "40-44",
      "45-49",
      "50-54",
      "55-59",
      "60-69",
      "70-79",
      "80-89",
      "90-99",
      "100-109",
      "110-119",
      "120-129",
      "130+",
    ];

    groupDisplay.sort(
      (a, b) => displayOrder.indexOf(a.label) - displayOrder.indexOf(b.label)
    );

    const MAX_VISIBLE = 12;

    const itemsHTML = groupDisplay
      .map(({ label, count, active, values }, idx) => {
        const joinedValues = values.join(","); // what will actually be sent to Searchspring
        const isDisabled = count === 0;
        const inputId = `filter--${facet.field}--${label.replace(
          /[^a-zA-Z0-9]/g,
          ""
        )}--searchspring`;
        const hiddenClass = idx >= MAX_VISIBLE ? "filter-item--hidden" : "";
        // Friendly label for UI
        const displayLabel =
          label.toLowerCase() === "under 20" ? 'Under 20"' : `${label}"`;

        return `
          <li class="filter-item filter-item-checkbox ${hiddenClass} ${
          isDisabled ? "filter-item--disabled" : ""
        }" data-type="checkbox" ${hiddenClass ? 'style="display:none;"' : ""}>
            <label
              data-filter
              data-filter-item-label
              class="filter-item__content filter-item__content--checkbox no-transition ${
                isDisabled ? "filter-item__content--disabled" : ""
              }"
              for="${inputId}"
            >
              <input
                type="checkbox"
                name="${facet.field}"
                data-value-escaped="${encodeURIComponent(joinedValues)}"
                value="${joinedValues}"
                class="filter-item__input visually-hidden"
                id="${inputId}"
                data-field="${facet.field}"
                data-value="${joinedValues}"
                ${active ? "checked" : ""}
                ${isDisabled ? "disabled" : ""}
                data-filter="${facet.field}=${joinedValues}"
                data-filter-item
              >
              <div class="filter-item__checkbox">
                <svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <span class="filter-item__label type-body-regular">${displayLabel}</span>
              <span class="filter-item__count">${count}</span>
            </label>
          </li>`;
      })
      .join("");

    const needsShowMore = groupDisplay.length > MAX_VISIBLE;
    const showMoreHTML = needsShowMore
      ? `<li class="filter-show-more"><a class="reset-all show-more-btn" style="cursor: pointer;" data-state="collapsed">View more</a></li>`
      : "";

    return `
      <ul class="filter-drawer__list">
        ${itemsHTML}
        ${showMoreHTML}
      </ul>
    `;
  },

  // Quickship special filter – consolidate lead time < 4 weeks into a single chip
  renderQuickshipFilter: (facet) => {
    // Define which lead-time strings qualify for Quickship (case-insensitive)
    const QS_VALUES = [
      "1-2 weeks",
      "1-3 weeks",
      "1-3 Weeks",
      "2-4 weeks",
      "3-4 weeks",
      "4 weeks",
    ];

    const quickshipValues = facet.values.filter((v) =>
      QS_VALUES.some((q) => q.toLowerCase() === String(v.value).toLowerCase())
    );

    const count = quickshipValues.reduce((sum, v) => sum + v.count, 0);
    const isActive = quickshipValues.some((v) => v.active);

    const valuesStr = QS_VALUES.join(",");
    const inputId = `filter--${facet.field}--quickship-searchspring`;
    const isDisabled = count === 0;

    return `
      <ul class="filter-drawer__list quickship-filter-list">
        <li class="filter-item filter-item-chip ${
          isDisabled ? "filter-item--disabled" : ""
        }" data-type="chip">
          <label
            data-filter
            data-filter-item-label
            class="filter-item__content filter-item__content--chip no-transition ${
              isDisabled ? "filter-item__content--disabled" : ""
            }"
            for="${inputId}"
          >
            
            <div class="filter-item__chip" tabindex="0" style="margin-left: 24px;" aria-label="Quickship">Quickship
            <input
              type="checkbox"
              name="${facet.field}"
              data-value-escaped="${encodeURIComponent(valuesStr)}"
              value="${valuesStr}"
              class="filter-item__input visually-hidden"
              id="${inputId}"
              data-field="${facet.field}"
              data-value="${valuesStr}"
              ${isActive ? "checked" : ""}
              ${isDisabled ? "disabled" : ""}
              data-filter="${facet.field}=${valuesStr}"
              data-filter-item
            >
            </div>
          </label>
        </li>
      </ul>
    `;
  },

  renderDropdownDimensionFilter: function (facet, dimensionType) {
    const numericVals = Array.from(
      new Set(
        (facet.values || [])
          .map((v) => parseFloat(v.value))
          .filter((n) => !isNaN(n))
      )
    ).sort((a, b) => a - b);

    if (!numericVals.length) return "";

    const optionHTML = numericVals
      .map((n) => `<option value="${n}">${n}</option>`)
      .join("");

    const reverseOptionHTML = numericVals
      .reverse()
      .map((n) => `<option value="${n}">${n}</option>`)
      .join("");

    return `
      <div class="dimension-filter-dropdown">
        <div class="dimension-inputs">
          
          <div class="dimension-input-group">
          <span class="dimension-label">Ft.</span>
            <select class="dimension-dropdown" data-field="${facet.field}" data-dimension="${dimensionType}" data-position="min">
              ${optionHTML}
            </select>
            <span class="select-arrow">▼</span>
            
          </div>
          <span class="dimension-to">to</span>
          <div class="dimension-input-group">
          <span class="dimension-label">Ft.</span>
            <select class="dimension-dropdown" data-field="${facet.field}" data-dimension="${dimensionType}" data-position="max">
               ${reverseOptionHTML}
            </select>
            <span class="select-arrow">▼</span>
            
          </div>
        </div>
      </div>
    `;
  },

  saveActiveFilters: function (activeFilters, collectionHandle = "") {
    try {
      const filtersObj = {};
      if (activeFilters && typeof activeFilters.forEach === "function") {
        activeFilters.forEach((vals, field) => {
          filtersObj[field] = vals;
        });
      }
      sessionStorage.setItem(
        "plpActiveFilters",
        JSON.stringify({
          handle: collectionHandle || window.location.pathname,
          filters: filtersObj,
          sortParam: window.utils.sortParam || "",
        })
      );
    } catch (err) {
      console.error("Error saving filter state", err);
    }
  },

  loadSavedFilters: function (currentHandle = "") {
    try {
      const raw = sessionStorage.getItem("plpActiveFilters");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const handleToCompare = currentHandle || window.location.pathname;
      if (parsed && parsed.handle === handleToCompare) return parsed;
      // handle mismatch – clear stale state and ignore.
      sessionStorage.removeItem("plpActiveFilters");
      return null;
    } catch (err) {
      console.error("Error loading filter state", err);
      return null;
    }
  },

  syncFilterUIWithActiveFilters: function (filtersContainer, activeFilters) {
    if (!filtersContainer || !(activeFilters instanceof Map)) return;

    activeFilters.forEach((values, field) => {
      values.forEach((value) => {
        // Checkbox inputs
        const checkbox = filtersContainer.querySelector(
          `input[data-field="${field}"][data-value="${value}"]`
        );
        if (checkbox) checkbox.checked = true;

        // Color swatches (or other chip-style filters)
        const swatch = filtersContainer.querySelector(
          `.filter-item__swatch[data-field="${field}"][data-value='${value}']`
        );
        if (swatch) swatch.classList.add("selected");
      });
    });
  },

  // === NEW helper: render checkboxes for ss_all_width_filter ===
  renderWidthRangeFilter: (facet) => {
    const MAX_VISIBLE = 12;

    const itemsHTML = (facet.values || [])
      .map((v, idx) => {
        const label = v.label || `${v.low}-${v.high}`;
        const isDisabled = v.count === 0 && !v.active;
        const inputId = `filter--${facet.field}--${label.replace(
          /[^a-zA-Z0-9]/g,
          ""
        )}--searchspring`;
        const hiddenClass = idx >= MAX_VISIBLE ? "filter-item--hidden" : "";

        // store low-high in value
        const valStr = `${v.low}-${v.high}`;

        return `
        <li class="filter-item filter-item-checkbox ${hiddenClass} ${
          isDisabled ? "filter-item--disabled" : ""
        }" data-type="checkbox" ${hiddenClass ? 'style="display:none;"' : ""}>
          <label data-filter data-filter-item-label class="filter-item__content filter-item__content--checkbox no-transition ${
            isDisabled ? "filter-item__content--disabled" : ""
          }" for="${inputId}">
            <input type="checkbox" name="${
              facet.field
            }" data-value-escaped="${encodeURIComponent(
          valStr
        )}" value="${valStr}" class="filter-item__input visually-hidden" id="${inputId}" data-field="${
          facet.field
        }" data-value="${valStr}" ${v.active ? "checked" : ""} ${
          isDisabled ? "disabled" : ""
        } data-filter="${facet.field}=${valStr}" data-filter-item>
            <div class="filter-item__checkbox"><svg class="icon icon-check" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1 3 2.5 2.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
            <span class="filter-item__label type-body-regular">${label}</span>
            <span class="filter-item__count">${v.count}</span>
          </label>
        </li>`;
      })
      .join("");

    const needsShowMore = (facet.values || []).length > MAX_VISIBLE;
    const showMoreHTML = needsShowMore
      ? `<li class="filter-show-more"><a class="reset-all show-more-btn" style="cursor: pointer;" data-state="collapsed">View more</a></li>`
      : "";

    return `<ul class="filter-drawer__list">${itemsHTML}${showMoreHTML}</ul>`;
  },
};
